import { normalizeEmailSignatures } from '../../utils/emailSignatures';
import { manualEntryToColumns } from '../../presence/presenceVocabulary';
import { SUPABASE_URL } from '../../utils/supabase/url';
/**
 * Centralised Data Service
 * Single source of truth for all application data with Google Drive persistence
 */

import type {
  Database,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters } from '../../types/claims';
import type { Document } from '../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../types/activity';
import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { getSupabaseClient } from '../../utils/supabase/client';
import { NetworkGateway } from '../NetworkGateway';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { buildDemoDatabase } from './demoFixture';
import { normalizeProjectStage } from '../../utils/stageUtils';
import { resolveProjectWorkspaceId } from './projectWorkspace';
import { projectRoleFieldsForBackend, projectRoleFieldsFromBackend } from './projectRoleFields';

import { SNAPSHOT_KEY, storedSnapshotOwner, validSnapshot, restoreSnapshot, persistSnapshot, clearSnapshot } from './snapshotCache';
const VERSION = '1393';
const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b`;


export abstract class DataServiceCore {
  protected db: Database;
  protected listeners: Set<() => void> = new Set();
  protected saveTimeout: NodeJS.Timeout | null = null;
  protected useGoogleDrive = false; // Will be set to true after Google Drive initialization
  protected useSupabase = true; // Use Supabase by default
  protected supabaseDisabledReason: string | null = null; // Track why Supabase was disabled
  // Demo mode — when true, the in-memory `db` holds the demo fixture, all
  // Supabase reads/writes are skipped, and localStorage persistence is
  // disabled so nothing leaks back into the real database.
  protected demoMode = false;
  protected supabaseRetryCount = 0;
  protected readonly MAX_SUPABASE_RETRIES = 3;
  protected isInitialized = false; // Track if data has been loaded
  protected initializationPromise: Promise<void> | null = null; // Track initialization promise
  protected realtimeChannels: RealtimeChannel[] = []; // For Realtime WebSocket subscriptions
  protected realtimeSubscribedUserId: string | null = null; // User the realtime channels were joined as
  protected realtimeRefreshTimeout: NodeJS.Timeout | null = null; // Debounce realtime refreshes
  protected lastBackendRefreshTime = 0; // Throttle backend refreshes
  protected readonly REFRESH_THROTTLE_MS = 10_000; // Minimum time between full refreshes
  protected retryCounters: Map<string, number> = new Map(); // Track retry attempts for failed saves
  protected readonly MAX_RETRIES = 3; // Maximum number of retry attempts
  // Records whose background write a snapshot may not have seen yet; see keepUnsyncedLocalWrites.
  private unsyncedWrites = new Map<string, { collection: keyof Database; id: string; pending: number; settledAt: number }>();
  // Orders write completions against snapshot requests without trusting clock resolution.
  private syncSequence = 0;
  // Debounce timer for listener notifications — prevents a storm of re-renders when
  // multiple entity writes happen in quick succession (e.g. batch load, Realtime burst).
  private notifyDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // ─── ABORT CONTROLLER for in-flight /database fetches ───────────────────────
  // Concurrent refresh callers share one /database request. When the page unloads we
  // abort immediately — so the browser sends a TCP FIN instead of silently
  // dropping the connection, which prevents the server-side EPIPE / broken pipe.
  private syncError: string | null = null;
  private refreshing = false;
  private retryAfter = 0;
  private initialLoadState: 'loading' | 'ready' | 'error' = 'loading';
  private snapshotOwner = storedSnapshotOwner();
  private snapshotRuntimeOwner: string | null = null;
  private identityGeneration = 0;
  private backendRequestStarted = false;
  private queuedForcedRefresh: Promise<void> | null = null;
  private backendRefreshPromise: Promise<void> | null = null;
  private activeFetchController: AbortController | null = null;

  /**
   * Create a new AbortController for a /database fetch, aborting any previous
   * in-flight request first.  Returns the signal to pass to fetch().
   */
  private createFetchController(timeoutMs = 30_000): AbortSignal {
    // Cancel the previous in-flight /database fetch (if any) so the browser
    // sends a clean TCP FIN → the server sees an AbortError and stops writing.
    this.activeFetchController?.abort();

    const controller = new AbortController();
    this.activeFetchController = controller;

    // Hard deadline so a hung backend can't hold the connection forever.
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    // Ensure the timeout is cleared if the signal is aborted for another reason
    controller.signal.addEventListener('abort', () => clearTimeout(timeoutId), { once: true });

    return controller.signal;
  }

  constructor() {
    this.db = this.createEmptyDatabase();
    
    // CRITICAL: Load from localStorage SYNCHRONOUSLY for instant 0ms data access
    const localData = this.loadFromLocalStorage();
    
    if (localData) {
      this.db = this.migrateDatabase(localData);
      this.initialLoadState = 'ready';
    }
    
    // Abort any in-flight /database fetch when the page is being unloaded.
    // This sends a proper TCP FIN so the server can detect the disconnect
    // before it finishes writing the response, preventing EPIPE errors.
    if (typeof globalThis.addEventListener === 'function') {
      const abortOnUnload = () => {
        this.activeFetchController?.abort();
        this.activeFetchController = null;
      };
      // pagehide fires reliably on mobile Safari; beforeunload covers desktop
      globalThis.addEventListener('pagehide', abortOnUnload);
      globalThis.addEventListener('beforeunload', abortOnUnload);
      // Also abort when the tab is hidden (e.g. user switches tabs on mobile)
      globalThis.document?.addEventListener('visibilitychange', () => {
        if (globalThis.document?.visibilityState === 'hidden') {
          abortOnUnload();
        }
      });
    }

    // Do not hit the authenticated /database endpoint until the app has a real
    // signed-in session. AuthContext triggers a refresh after login and after
    // session restoration, which keeps startup quiet on the login screen.
    const generation = this.identityGeneration;
    this.initializationPromise = restoreSnapshot(this.snapshotOwner, VERSION).then(data => {
      if (data && generation === this.identityGeneration && storedSnapshotOwner() === this.snapshotOwner) {
        this.db = this.migrateDatabase(data);
        this.initialLoadState = 'ready';
        this.notifyListeners();
      }
      this.isInitialized = true;
      this.notifyListeners();
    }).catch(() => {
      this.isInitialized = true;
      if (this.initialLoadState === 'loading') this.initialLoadState = 'error';
      this.syncError = 'Your saved dashboard could not be restored. Please try again.';
      this.notifyListeners();
    });
    this.bindRealtimeToAuth(); // Start Realtime WebSocket subscriptions once a session exists

    // A tab returning to the foreground may have missed realtime events
    // (background tabs lose their websocket, especially on mobile Safari).
    // Refresh once per activation; refreshFromBackend throttles itself and
    // no-ops without a session.
    if (typeof window !== 'undefined') {
      NetworkGateway.register('data-service-realtime', {
        onActivate: () => { void this.refreshFromBackend().catch(() => {}); },
        onDeactivate: () => {},
      });
    }
  }

  /**
   * Postgres-changes subscriptions are RLS-gated per subscriber and the
   * claims are captured when the channel joins. Subscribing straight from the
   * constructor raced session restoration: a tab whose channels joined before
   * the session restored subscribed as `anon`, auth.uid() evaluated NULL, and
   * RLS-gated tables (projects, notifications, ...) silently delivered no
   * events for the lifetime of the tab — the "list doesn't update until I
   * refresh" reports. Rebuild the channels whenever the signed-in user
   * changes so subscriptions always carry authenticated claims.
   */
  protected bindRealtimeToAuth(): void {
    if (!this.useSupabase) return;
    const supabase = getSupabaseClient();
    supabase.auth.onAuthStateChange((event, session) => {
      if (event !== 'INITIAL_SESSION' && event !== 'SIGNED_IN' && event !== 'SIGNED_OUT') return;
      const uid = session?.user?.id ?? null;
      if (uid && this.snapshotRuntimeOwner !== uid) this.retryAfter = 0;
      this.snapshotRuntimeOwner = uid;
      if (uid !== this.snapshotOwner || event === 'SIGNED_OUT') {
        this.identityGeneration++;
        this.snapshotOwner = uid;
        this.activeFetchController?.abort();
        this.backendRefreshPromise = null;
        this.queuedForcedRefresh = null;
        this.backendRequestStarted = false;
        this.lastBackendRefreshTime = 0;
        this.retryAfter = 0;
        this.syncError = null;
        this.refreshing = false;
        this.unsyncedWrites.clear();
        this.db = this.createEmptyDatabase();
        this.initialLoadState = 'loading';
        clearSnapshot();
        this.notifyListeners();
      }
      if (uid === this.realtimeSubscribedUserId) return;
      this.realtimeSubscribedUserId = uid;

      this.realtimeChannels.forEach(channel => {
        try { supabase.removeChannel(channel); } catch { /* noop */ }
      });
      this.realtimeChannels = [];

      if (!uid || this.demoMode) return;
      this.setupRealtimeSubscriptions();
      // Events may have been missed while the channels were down — converge once.
      this.debouncedRealtimeRefresh();
    });
  }

  /**
   * Merge any locally-created planner items that haven't made it to Supabase yet
   * into a freshly-fetched remote database snapshot. Prevents tasks from disappearing
   * during the window between local creation and successful backend persistence.
   */
  protected mergeLocalPlannerItems(remoteDb: any): void {
    const RECENT_THRESHOLD_MS = 60_000; // 60 seconds
    const now = Date.now();
    const localItems: Record<string, any> = this.db.projectPlannerItems || {};
    const remoteItems: Record<string, any> = remoteDb.projectPlannerItems || {};

    Object.keys(localItems).forEach(id => {
      if (!remoteItems[id]) {
        const local = localItems[id];
        const updatedAt = local.updatedAt ? new Date(local.updatedAt).getTime() : 0;
        if (now - updatedAt < RECENT_THRESHOLD_MS) {
          // Item was created/updated locally in the last 60s but isn't in Supabase yet —
          // keep it so it doesn't vanish while the backend save is in-flight or retrying.
          remoteItems[id] = local;
        }
      }
    });

    remoteDb.projectPlannerItems = remoteItems;
  }

  /**
   * Mark a record as changed locally while its backend write runs in the
   * background, so a snapshot that may predate the write cannot replace it.
   */
  protected trackLocalWrite(collection: keyof Database, id: string, write: Promise<unknown>): void {
    const key = `${collection}:${id}`;
    const entry = this.unsyncedWrites.get(key) ?? { collection, id, pending: 0, settledAt: 0 };
    entry.pending += 1;
    this.unsyncedWrites.set(key, entry);
    const settle = () => { entry.pending -= 1; entry.settledAt = ++this.syncSequence; };
    void write.then(settle, settle);
  }

  /**
   * Carry locally written records into a freshly-fetched snapshot when the
   * snapshot may have been read before their write landed. A snapshot can be
   * requested mid-write and answered after it: an internal user's takes 13-15 s,
   * and an employer's contacts are upserted one by one behind the PUT whose
   * realtime event starts one. Such a record keeps its local copy, colleagues'
   * edits to it included, until a snapshot requested after the write lands.
   */
  protected keepUnsyncedLocalWrites(remoteDb: any, requestedAt: number): void {
    this.unsyncedWrites.forEach((write, key) => {
      if (write.pending === 0 && write.settledAt < requestedAt) {
        // It landed before this snapshot was requested, so the snapshot has it.
        this.unsyncedWrites.delete(key);
        return;
      }
      const local = (this.db as any)[write.collection]?.[write.id];
      if (local) (remoteDb[write.collection] ??= {})[write.id] = local;
    });
  }

  /**
   * Disable Supabase sync after a hard network failure to avoid repeated console noise
   */
  protected disableSupabase(reason: string): void {
    if (!this.useSupabase) return;
    this.useSupabase = false;
    this.supabaseDisabledReason = reason;

    // Tear down realtime channels to avoid reconnect attempts
    this.realtimeChannels.forEach(channel => {
      try { channel.unsubscribe(); } catch { /* noop */ }
    });
    this.realtimeChannels = [];

    console.warn(`⚠️ Supabase disabled; using local cache only. Reason: ${reason}`);
  }

  /**
   * Resolve the best available auth token for backend and realtime calls.
   * Falls back to anon key if no user session exists.
   */
  protected async getAuthHeaderToken(): Promise<string> {
    try {
      const supabase = getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || publicAnonKey;
    } catch {
      return publicAnonKey;
    }
  }

  /**
   * Resolve a real signed-in user's access token.
   * The /database snapshot endpoint is intentionally authenticated; calling it
   * with the anon key before login only produces a 401 and noisy console output.
   */
  protected async getSessionAccessToken(): Promise<string | null> {
    try {
      const supabase = getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token ?? null;
    } catch {
      return null;
    }
  }

  public abstract getMainContractor(id: string): MainContractor | undefined;
  public abstract getMainContractors(): MainContractor[];

  // ============================================================================
  // INITIALIZATION & PERSISTENCE
  // ============================================================================

  /**
   * Sync from Supabase in background (async, non-blocking)
   */
  protected async syncFromSupabase(): Promise<void> {
    await this.refreshFromBackend();
    this.isInitialized = true;
  }

  /**
   * Migrate old database format to new format
   */
  protected migrateDatabase(db: any): Database {
    // CRITICAL: Purge legacy non-UUID users and data to prevent backend errors
    // BUT allow 'user-' prefix which is used for seed data and legacy migrations
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (db.users) {
      Object.keys(db.users).forEach(userId => {
        const isValid = uuidRegex.test(userId) || userId.startsWith('user-');
        
        if (!isValid) {
          console.warn(`Purging legacy user from local cache: ${userId}`);
          delete db.users[userId];
        }
      });
    }

    // Clean up orphaned notifications and legacy user IDs
    if (db.notifications) {
      let cleanedCount = 0;
      Object.keys(db.notifications).forEach(notifId => {
        const notification = db.notifications[notifId];
        
        // Remove if user ID is invalid (legacy) but allow 'user-' prefix
        if (notification.userId && !uuidRegex.test(notification.userId) && !notification.userId.startsWith('user-')) {
          delete db.notifications[notifId];
          cleanedCount++;
          return;
        }

        // Remove if user no longer exists in local DB
        if (db.users && notification.userId && !db.users[notification.userId]) {
          delete db.notifications[notifId];
          cleanedCount++;
        }
      });
      if (cleanedCount > 0) {
      }
    }
    
    // Ensure new fields exist
    if (!db.documents) {
      db.documents = {};
    }
    if (!db.pendingUploads) {
      db.pendingUploads = {};
    }
    if (!db.claims) {
      db.claims = {};
    }
    if (!db.claimCorrespondence) {
      db.claimCorrespondence = {};
    }
    if (!db.invoiceCache) {
      db.invoiceCache = {};
    }
    if (!db.taskComments) {
      db.taskComments = {};
    }
    if (!db.projectPlannerItems) {
      db.projectPlannerItems = {};
    }
    if (!db.dailyDiaries) {
      db.dailyDiaries = {};
    }
    if (!db.dayworkEntries) {
      db.dayworkEntries = {};
    }
    if (!db.progressReports) {
      db.progressReports = {};
    }
    if (!db.siteDeliveries) {
      db.siteDeliveries = {};
    }
    if (!db.variationReports) {
      db.variationReports = {};
    }

    // Migrate projects to include team relationship fields
    if (db.projects) {
      Object.keys(db.projects).forEach(projectId => {
        const project = db.projects[projectId];
        if (!project.mainContractorContactIds) {
          project.mainContractorContactIds = [];
        }
        if (!project.assignedSupervisorIds) {
          project.assignedSupervisorIds = [];
        }
      });
    }
    
    // Migrate tasks to ensure they have a status field
    if (db.tasks) {
      let fixedCount = 0;
      let enrichedCount = 0;
      Object.keys(db.tasks).forEach(taskId => {
        const task = db.tasks[taskId];
        if (!task.status) {
          task.status = 'pending';
          fixedCount++;
        }
        
        // Enrich task with user and project names from the loaded data
        if (task.created_by && !task.created_by_name && db.users && db.users[task.created_by]) {
          task.created_by_name = db.users[task.created_by].name;
          enrichedCount++;
        }
        
        if (task.assigned_to && !task.assigned_to_name && db.users && db.users[task.assigned_to]) {
          task.assigned_to_name = db.users[task.assigned_to].name;
          enrichedCount++;
        }
        
        if (task.project_id && !task.project_name && db.projects && db.projects[task.project_id]) {
          const project = db.projects[task.project_id];
          task.project_name = project.name;
          task.client_company_id = project.clientId;
          
          if (db.companies && db.companies[project.clientId]) {
            task.client_name = db.companies[project.clientId].name;
          }
          enrichedCount++;
        }
      });
      // Tasks migrated silently - only log errors
    }
    
    // Initialize sample employers if none exist
    if (!db.employers || Object.keys(db.employers).length === 0) {
      db.employers = {};
      this.initializeSampleEmployers(db);
    }
    
    // Initialize sample rate library if none exists
    if (!db.rateLibraries || Object.keys(db.rateLibraries).length === 0) {
      db.rateLibraries = {};
      db.rateLibraryItems = {};
      this.initializeSampleRateLibrary(db);
    }
    
    return db;
  }

  /**
   * Initialize sample employers for demo purposes
   */
  protected initializeSampleEmployers(db: any): void {
    const now = new Date().toISOString();
    const sampleEmployers = [
      {
        id: 'emp-galliford-try',
        name: 'Galliford Try',
        type: 'main_contractor' as const,
        paymentTerms: '45 days from certification',
        avgDaysToPay: 42,
        paymentReliability: 'good' as const,
        creditworthinessScore: 72,
        creditworthinessGrade: 'B' as const,
        disputeHistory: 3,
        activeProjectsWithClients: 5,
        createdAt: now,
        updatedAt: now,
      },
      {
        id: 'emp-balfour-beatty',
        name: 'Balfour Beatty',
        type: 'main_contractor' as const,
        paymentTerms: '30 days from certification',
        avgDaysToPay: 28,
        paymentReliability: 'excellent' as const,
        creditworthinessScore: 88,
        creditworthinessGrade: 'A' as const,
        disputeHistory: 0,
        activeProjectsWithClients: 8,
        createdAt: now,
        updatedAt: now,
      },
      {
        id: 'emp-laing-orourke',
        name: "Laing O'Rourke",
        type: 'main_contractor' as const,
        paymentTerms: '35 days from certification',
        avgDaysToPay: 38,
        paymentReliability: 'good' as const,
        creditworthinessScore: 75,
        creditworthinessGrade: 'B' as const,
        disputeHistory: 1,
        activeProjectsWithClients: 6,
        createdAt: now,
        updatedAt: now,
      },
    ];
    
    sampleEmployers.forEach(emp => {
      db.employers[emp.id] = emp;
    });
  }

  /**
   * Initialize sample rate library for demo purposes
   */
  protected initializeSampleRateLibrary(db: any): void {
    const now = new Date().toISOString();
    
    // Create a global Contract Group rate library
    const libraryId = 'lib-contract-group-2024';
    db.rateLibraries[libraryId] = {
      id: libraryId,
      name: 'Contract Group Standard Rates',
      description: '2024 Q4 Standard Rate Library',
      category: 'Standard',
      companyId: null, // Global library
      isPublic: true,
      rates: [],
      lastUpdated: now,
      createdAt: now,
      createdBy: 'system',
    };
    
    // Add sample rate library items
    const sampleItems = [
      {
        id: 'rate-groundworks-001',
        libraryId,
        code: 'A.01.010',
        description: 'Excavation in firm ground (per m³)',
        unit: 'm³',
        labourRate:12.50,
        materialRate: 0,
        plantRate: 8.75,
        subcontractorRate: 0,
        totalRate: 21.25,
        ricsBenchmarkCode: 'RICS-EXC-001',
        varianceFromBenchmark: 3.5,
        wastePercentage: 5,
        productivityFactor: 1.0,
        usageCount: 45,
        lastUsedAt: now,
        lastUpdatedCost: now,
        status: 'active' as const,
        confidenceLevel: 'high' as const,
        notes: 'Standard rate for firm ground excavation',
        createdAt: now,
        updatedAt: now,
      },
      {
        id: 'rate-concrete-001',
        libraryId,
        code: 'E.10.020',
        description: 'Ready-mix concrete C35 (per m³)',
        unit: 'm³',
        labourRate:8.00,
        materialRate: 95.00,
        plantRate: 12.00,
        subcontractorRate: 0,
        totalRate: 115.00,
        ricsBenchmarkCode: 'RICS-CON-C35',
        varianceFromBenchmark: -4.2,
        wastePercentage: 2.5,
        productivityFactor: 1.0,
        usageCount: 68,
        lastUsedAt: now,
        lastUpdatedCost: now,
        status: 'active' as const,
        confidenceLevel: 'high' as const,
        notes: 'Bulk discount secured with supplier',
        createdAt: now,
        updatedAt: now,
      },
      {
        id: 'rate-steel-001',
        libraryId,
        code: 'G.20.010',
        description: 'Structural steel frame (per tonne)',
        unit: 'tonne',
        labourRate:450.00,
        materialRate: 1650.00,
        plantRate: 250.00,
        subcontractorRate: 100.00,
        totalRate: 2450.00,
        ricsBenchmarkCode: 'RICS-STL-FRM',
        varianceFromBenchmark: 7.9,
        wastePercentage: 3,
        productivityFactor: 0.95,
        usageCount: 32,
        lastUsedAt: now,
        lastUpdatedCost: now,
        status: 'active' as const,
        confidenceLevel: 'medium' as const,
        notes: 'Premium for CE marking and fabrication lead times',
        createdAt: now,
        updatedAt: now,
      },
    ];
    
    sampleItems.forEach(item => {
      db.rateLibraryItems[item.id] = item;
    });
  }

  /**
   * Enable Google Drive synchronization (DEPRECATED - now using Supabase)
   */
  public async enableGoogleDrive(_clientId: string, _sharedDriveId?: string): Promise<void> {
    // Silently does nothing - Google Drive database storage is deprecated, now using Supabase
    return Promise.resolve();
  }

  /**
   * Disable Google Drive and use localStorage only (DEPRECATED - now using Supabase)
   */
  public disableGoogleDrive(): void {
    // Silently does nothing - Google Drive database storage is deprecated, now using Supabase
  }

  /**
   * Check if Google Drive is enabled (DEPRECATED - now using Supabase)
   */
  public isGoogleDriveEnabled(): boolean {
    return false; // Always false as Google Drive database storage is deprecated
  }

  /**
   * Get the entire database (for backups, exports, etc.)
   */
  public getDatabase(): Database {
    return this.db;
  }

  protected createEmptyDatabase(): Database {
    return {
      // Core Entities
      companies: {},
      users: {},
      projects: {},
      tenders: {},
      contractItems: {},
      interimApplications: {},
      retentionLedgers: {},
      tasks: {},
      projectPlannerItems: {},
      taskComments: {},
      notifications: {},
      shareTokens: {},
      upcomingItems: {},
      risks: {},
      
      // Estimating & Resources
      estimatingTeam: {},
      suppliers: {},
      termsTemplates: {},
      
      // Rate Libraries & Benchmarking
      rateLibraries: {},
      rateLibraryItems: {},
      ricsBenchmarks: {},
      commercialHealthMetrics: {},
      
      // Contractors & Employers
      mainContractors: {},
      employers: {},
      clientEmployerRelationships: {},
      entitlementAlerts: {},
      activeDisputes: {},
      
      // CRM Contacts
      contacts: {},
      
      // Client Request Management
      clientRequests: {},
      
      // Supervisor Site Records
      dailyDiaries: {},
      dayworkEntries: {},
      progressReports: {},
      siteDeliveries: {},
      variationReports: {},

      // Claims & Disputes
      claims: {},
      claimCorrespondence: {},
      
      // Activity & Collaboration System
      activities: {},
      
      // System Activity Logs (immutable audit trail)
      systemActivityLogs: {},
      
      // Documents
      documents: {},
      pendingUploads: {},
      
      // Invoice Cache
      invoiceCache: {},
      
      // Archive storage
      archivedData: {},
      lifecyclePolicies: {},
      lifecycleLogs: {},
      
      // Metadata
      lastUpdated: new Date().toISOString(),
      version: VERSION,
      totalSizeBytes: 0,
      archivedSizeBytes: 0,
    };
  }

  protected loadFromLocalStorage(): Database | null {
    try {
      return validSnapshot(JSON.parse(localStorage.getItem(SNAPSHOT_KEY) || 'null'), this.snapshotOwner, VERSION);
    } catch { return null; }
  }

  protected saveToLocalStorage(): void {
    if (this.demoMode || !this.snapshotOwner || this.snapshotRuntimeOwner !== this.snapshotOwner) return;
    this.db.lastUpdated = new Date().toISOString();
    this.db.version = VERSION;
    void persistSnapshot(this.db, this.snapshotOwner);
  }

  /**
   * Save to appropriate storage (Supabase, Google Drive, or localStorage)
   * Uses debouncing to avoid too many saves
   */
  protected saveToStorage(): void {
    // Always save to localStorage for quick access
    this.saveToLocalStorage();

    // Note: Individual entity saves to Supabase are handled by saveEntityToBackend()
    // The bulk database save is no longer needed with the SQL backend
  }

  /**
   * Convert a stage to the id stored in projects.project_stage.
   * The column's CHECK constraint accepts only canonical ids, so legacy
   * aliases are collapsed to them and canonical ids pass through untouched.
   */
  protected convertStageToBackend(stage: string): string {
    const stageMap: Record<string, string> = {
      'new_enquiry': 'itt_received',
      'enquiry_received': 'itt_received',
      'in_progress': 'tendering',
      'tender_in_progress': 'tendering',
      'with_client': 'internal_review',
      'client_review': 'internal_review',
      'finalising_submission': 'cleared_for_submission',
      'ready_to_submit': 'cleared_for_submission',
      'submitted': 'tender_submitted',
      'pre_contract': 'pre_start',
      'construction': 'on_site',
      'retention': 'defects_liability',
      'retention_period': 'defects_liability',
      'complete': 'closed',
    };

    return stageMap[stage] || stage;
  }

  /**
   * Convert backend stage format to frontend format
   * Maps new DB values (enquiry_received, tender_in_progress, etc.) to frontend enum (new_enquiry, in_progress, etc.)
   */
  protected convertStageFromBackend(stage: string): string {
    const stageMap: Record<string, string> = {
      // New DB values → Frontend enum
      'enquiry_received': 'new_enquiry',
      'tender_in_progress': 'in_progress',
      'client_review': 'with_client',
      'ready_to_submit': 'finalising_submission',
      'submitted': 'tender_submitted',
      'on_hold': 'on_hold',
      'pre_contract': 'pre_contract',
      'on_site': 'construction',
      'final_account': 'final_account',
      'retention_period': 'retention',
      'closed': 'complete',
      
      // Backwards-compat: Old DB values (just in case)
      'On_Hold': 'on_hold',
    };
    
    return stageMap[stage] || stage;
  }

  /**
   * Helper method to save individual entity to backend immediately
   */
  protected async saveEntityToBackend(collection: string, id: string, data: any, isNew: boolean = false): Promise<void> {
    if (!this.useSupabase) {
      return;
    }

    try {
      // Transform data based on collection type to match backend schema
      const transformedData = this.transformDataForBackend(collection, data);
      
      // For POST requests, remove auto-generated fields that the backend will create
      let dataToSend = transformedData;
      if (isNew && collection === 'notifications') {
        dataToSend = { ...transformedData };
        // Backend will generate these with DEFAULT values, so don't send them on POST
        delete dataToSend.id;
        delete dataToSend.created_at;
      }
      
      const url = isNew 
        ? `${API_BASE}/${collection}`
        : `${API_BASE}/${collection}/${id}`;
      
      const method = isNew ? 'POST' : 'PUT';

      const token = await this.getAuthHeaderToken();

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(dataToSend),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        
        console.error(`❌ [DataService] Backend ${method} failed for ${collection}/${id}:`, {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText,
          url: url,
          method: method,
          dataSent: dataToSend,
        });
        
        // If PUT fails with 404, the item doesn't exist in backend - retry as POST
        if (!isNew && response.status === 404 && errorText.includes('Item not found')) {
          console.warn(`⚠️  ${collection} ${id} not found in backend, creating it instead...`);
          await this.saveEntityToBackend(collection, id, data, true);
          return;
        }
        
        // Special handling for activities with foreign key errors - retry later with limit
        if (collection === 'activities' && errorText.includes('does not exist in projects table')) {
          const retryKey = `${collection}-${id}`;
          const retryCount = this.retryCounters.get(retryKey) || 0;
          
          if (retryCount < this.MAX_RETRIES) {
            this.retryCounters.set(retryKey, retryCount + 1);
            console.warn(`⚠️  Activity references non-existent project, retry ${retryCount + 1}/${this.MAX_RETRIES} in 5 seconds:`, id);
            setTimeout(() => {
              this.saveEntityToBackend(collection, id, data, isNew);
            }, 5000);
            return; // Don't log error, we'll retry
          } else {
            // Max retries reached - delete the orphaned activity
            console.error(`❌ Activity ${id} failed after ${this.MAX_RETRIES} retries. Deleting orphaned activity from local storage.`);
            this.retryCounters.delete(retryKey);
            delete this.db.activities[id];
            this.saveToLocalStorage();
            this.notifyListeners();
            return;
          }
        }
        
        // Special handling for main contractor contacts with FK errors - retry later with limit
        if (collection === 'mainContractorContacts' && (errorText.includes('main_contractor_id') || errorText.includes('foreign key'))) {
          const retryKey = `${collection}-${id}`;
          const retryCount = this.retryCounters.get(retryKey) || 0;
          
          if (retryCount < this.MAX_RETRIES) {
            this.retryCounters.set(retryKey, retryCount + 1);
            console.warn(`⚠️  Main contractor contact references non-existent contractor, retry ${retryCount + 1}/${this.MAX_RETRIES} in 2 seconds:`, id);
            setTimeout(() => {
              this.saveEntityToBackend(collection, id, data, isNew);
            }, 2000);
            return; // Don't log error, we'll retry
          } else {
            // Max retries reached - delete the orphaned contact
            console.error(`❌ Main contractor contact ${id} failed after ${this.MAX_RETRIES} retries. Deleting orphaned contact from local storage.`);
            this.retryCounters.delete(retryKey);
            delete (this.db.mainContractorContacts as Record<string, unknown>)[id];
            this.saveToLocalStorage();
            this.notifyListeners();
            return;
          }
        }
        
        // Special handling for notifications with user FK errors - retry later with limit
        if (collection === 'notifications' && (errorText.includes('Invalid notification data') || errorText.includes('user_id') || errorText.includes('foreign key'))) {
          const retryKey = `${collection}-${id}`;
          const retryCount = this.retryCounters.get(retryKey) || 0;
          
          if (retryCount < this.MAX_RETRIES) {
            this.retryCounters.set(retryKey, retryCount + 1);
            // Silently retry - user creation is probably still in progress
            setTimeout(() => {
              this.saveEntityToBackend(collection, id, data, isNew);
            }, 3000);
            return; // Don't log error, we'll retry
          } else {
            // Max retries reached - delete the orphaned notification silently
            this.retryCounters.delete(retryKey);
            delete this.db.notifications[id];
            this.saveToLocalStorage();
            this.notifyListeners();
            return;
          }
        }
        
        // Only log errors for critical collections (notifications auto-retry silently)
        if (collection !== 'notifications') {
          console.error(`❌ Backend ${method} ${collection} failed:`, response.status, errorText);
        }
        if (collection === 'tasks') {
          console.error('❌ Failed task data:', transformedData);
        }
        if (collection === 'mainContractors') {
          console.error('❌ Failed main contractor data:', transformedData);
          console.error('❌ Full response error:', errorText);
        }

        throw new Error(`Backend ${method} failed for ${collection}/${id}: ${response.status} ${response.statusText}`);
      } else {
        // Success - clear retry counter if it exists
        const retryKey = `${collection}-${id}`;
        if (this.retryCounters.has(retryKey)) {
          this.retryCounters.delete(retryKey);
        }
        this.supabaseRetryCount = 0;
        this.supabaseDisabledReason = null;
      }
    } catch (error) {
      // Handle network errors with retry logic for notifications, activities, and contacts
      if (collection === 'notifications' || collection === 'activities' || collection === 'mainContractorContacts') {
        const retryKey = `${collection}-${id}`;
        const retryCount = this.retryCounters.get(retryKey) || 0;
        
        if (retryCount < this.MAX_RETRIES) {
          this.retryCounters.set(retryKey, retryCount + 1);
          const delays = { notifications: 3000, activities: 5000, mainContractorContacts: 2000 };
          const delay = delays[collection as keyof typeof delays] || 3000;
          console.warn(`⚠️  Network error saving ${collection}, retry ${retryCount + 1}/${this.MAX_RETRIES} in ${delay/1000} seconds:`, id);
          setTimeout(() => {
            this.saveEntityToBackend(collection, id, data, isNew);
          }, delay);
          return;
        } else {
          // Max retries reached - delete the orphaned record
          console.error(`❌ ${collection} ${id} failed after ${this.MAX_RETRIES} retries. Deleting orphaned record from local storage.`);
          this.retryCounters.delete(retryKey);
          if (collection === 'notifications') delete this.db.notifications[id];
          if (collection === 'activities') delete this.db.activities[id];
          if (collection === 'mainContractorContacts') delete (this.db.mainContractorContacts as Record<string, unknown>)[id];
          this.saveToLocalStorage();
          this.notifyListeners();
          return;
        }
      }
      
      console.error(`❌ Error saving ${collection} to backend:`, error);
      const message = typeof (error as any)?.message === 'string' ? (error as any).message : '';
      if (message.includes('connection closed') || message.includes('Failed to fetch') || (error as any)?.name === 'AbortError') {
        this.supabaseRetryCount += 1;
        const attempts = `${this.supabaseRetryCount}/${this.MAX_SUPABASE_RETRIES}`;
        console.warn(`⚠️ Supabase save failed (attempt ${attempts}): ${message}`);
        if (this.supabaseRetryCount >= this.MAX_SUPABASE_RETRIES) {
          this.disableSupabase(message || 'network error');
        }
      }
      throw error;
    }
  }

  /**
   * Transform local data to match backend database schema
   */
  protected transformDataForBackend(collection: string, data: any): any {
    switch (collection) {
      case 'companies':
        return this.transformCompanyForBackend(data);
      case 'notifications':
        return this.transformNotificationForBackend(data);
      case 'activities':
        return this.transformActivityForBackend(data);
      case 'projects':
        return this.transformProjectForBackend(data);
      case 'users':
        return this.transformUserForBackend(data);
      case 'client_requests':
        return this.transformClientRequestForBackend(data);
      case 'tasks':
        return this.transformTaskForBackend(data);
      case 'projectPlannerItems':
        return this.transformProjectPlannerItemForBackend(data);
      case 'taskComments':
        return this.transformTaskCommentForBackend(data);
      case 'mainContractors':
        return this.transformMainContractorForBackend(data);
      case 'mainContractorContacts':
        return this.transformMainContractorContactForBackend(data);
      default:
        return data; // No transformation needed
    }
  }

  /**
   * Transform backend database data to match frontend format
   */
  protected transformDataFromBackend(data: any): any {
    // Transform activities from backend camelCase to frontend snake_case
    if (data.activities) {
      const transformedActivities: any = {};
      Object.keys(data.activities).forEach(activityId => {
        const activity = data.activities[activityId];
        transformedActivities[activityId] = {
          ...activity,
          // Map camelCase backend fields to snake_case frontend fields
          related_to_type: activity.relatedToType || activity.related_to_type,
          related_to_id: activity.relatedToId || activity.related_to_id,
          related_to_name: activity.relatedToName || activity.related_to_name,
          created_by_id: activity.createdById || activity.created_by_id,
          created_by_name: activity.createdByName || activity.created_by_name,
          created_by_email: activity.createdByEmail || activity.created_by_email,
          created_at: activity.createdAt || activity.created_at,
          call_duration_minutes: activity.callDurationMinutes || activity.call_duration_minutes,
          call_contact_name: activity.callContactName || activity.call_contact_name,
          call_contact_phone: activity.callContactPhone || activity.call_contact_phone,
          call_outcome: activity.callOutcome || activity.call_outcome,
          meeting_start_time: activity.meetingStartTime || activity.meeting_start_time,
          meeting_end_time: activity.meetingEndTime || activity.meeting_end_time,
          meeting_location: activity.meetingLocation || activity.meeting_location,
          meeting_attendees: activity.meetingAttendees || activity.meeting_attendees,
          meeting_type: activity.meetingType || activity.meeting_type,
          email_to: activity.emailTo || activity.email_to,
          email_cc: activity.emailCc || activity.email_cc,
          email_subject: activity.emailSubject || activity.email_subject,
          email_sent_at: activity.emailSentAt || activity.email_sent_at,
          file_url: activity.fileUrl || activity.file_url,
          file_name: activity.fileName || activity.file_name,
          file_size: activity.fileSize || activity.file_size,
          file_type: activity.fileType || activity.file_type,
          status_from: activity.statusFrom || activity.status_from,
          status_to: activity.statusTo || activity.status_to,
          document_id: activity.documentId || activity.document_id,
          document_name: activity.documentName || activity.document_name,
          document_size: activity.documentSize || activity.document_size,
          document_category: activity.documentCategory || activity.document_category,
          change_type: activity.changeType || activity.change_type,
          change_field: activity.changeField || activity.change_field,
          change_old_value: activity.changeOldValue || activity.change_old_value,
          change_new_value: activity.changeNewValue || activity.change_new_value,
          replies_count: activity.repliesCount || activity.replies_count,
          parent_activity_id: activity.parentActivityId || activity.parent_activity_id,
          is_internal: activity.isInternal !== undefined ? activity.isInternal : activity.is_internal,
          is_pinned: activity.isPinned !== undefined ? activity.isPinned : activity.is_pinned,
          requires_follow_up: activity.requiresFollowUp !== undefined ? activity.requiresFollowUp : activity.requires_follow_up,
          follow_up_date: activity.followUpDate || activity.follow_up_date,
          follow_up_assigned_to: activity.followUpAssignedTo || activity.follow_up_assigned_to,
          is_resolved: activity.isResolved !== undefined ? activity.isResolved : activity.is_resolved,
          company_id: activity.companyId || activity.company_id,
          project_id: activity.projectId || activity.project_id,
          client_id: activity.clientId || activity.client_id,
          contact_id: activity.contactId || activity.contact_id,
          tender_id: activity.tenderId || activity.tender_id,
          main_contractor_id: activity.mainContractorId || activity.main_contractor_id,
        };
      });
      data.activities = transformedActivities;
    }
    
    // Enrich activities with user names from users table
    if (data.activities && data.users) {
      Object.keys(data.activities).forEach(activityId => {
        const activity = data.activities[activityId];
        // If created_by_name looks like a user ID (starts with "user-"), try to resolve it
        if (activity.created_by_id && (!activity.created_by_name || activity.created_by_name === activity.created_by_id)) {
          const user = data.users[activity.created_by_id];
          if (user) {
            activity.created_by_name = user.name;
            activity.created_by_email = user.email || activity.created_by_email;
          }
        }
      });
    }

    // Transform client requests from backend camelCase to the legacy frontend
    // shape used by the workspace approval UI.
    if (data.clientRequests) {
      const transformedClientRequests: any = {};
      Object.keys(data.clientRequests).forEach(requestId => {
        const request = data.clientRequests[requestId];
        transformedClientRequests[requestId] = {
          ...request,
          company_name: request.company_name ?? request.companyName ?? '',
          company_address: request.company_address ?? request.companyAddress ?? '',
          company_postcode: request.company_postcode ?? request.companyPostcode ?? '',
          company_phone: request.company_phone ?? request.companyPhone ?? '',
          company_email: request.company_email ?? request.companyEmail ?? '',
          company_number: request.company_number ?? request.companyNumber ?? undefined,
          contact_name: request.contact_name ?? request.contactName ?? '',
          contact_email: request.contact_email ?? request.contactEmail ?? '',
          contact_phone: request.contact_phone ?? request.contactPhone ?? '',
          contact_position: request.contact_position ?? request.contactPosition ?? '',
          billing_tier: request.billing_tier ?? request.billingTier ?? 'silver',
          billing_frequency: request.billing_frequency ?? request.billingFrequency ?? 'monthly',
          subscription_price: request.subscription_price ?? request.subscriptionPrice ?? 0,
          requestedBy: request.requestedBy ?? request.requested_by ?? '',
          requestedByName: request.requestedByName ?? request.requested_by_name ?? '',
          requestReason: request.requestReason ?? request.request_reason ?? '',
          reviewedBy: request.reviewedBy ?? request.reviewed_by,
          reviewedByName: request.reviewedByName ?? request.reviewed_by_name,
          reviewedAt: request.reviewedAt ?? request.reviewed_at,
          reviewNotes: request.reviewNotes ?? request.review_notes,
          createdClientId: request.createdClientId ?? request.created_client_id,
          createdAt: request.createdAt ?? request.created_at,
          updatedAt: request.updatedAt ?? request.updated_at,
        };
      });
      data.clientRequests = transformedClientRequests;
    }

    // Transform users from backend format to frontend format
    if (data.users) {
      const transformedUsers: any = {};
      Object.keys(data.users).forEach(userId => {
        const user = data.users[userId];
        

        
        // Ensure assignedProjects is an array
        let assignedProjects = user.assignedProjects || user.assigned_projects || [];
        if (typeof assignedProjects === 'string') {
          try {
            assignedProjects = JSON.parse(assignedProjects);
          } catch (e) {
            assignedProjects = [];
          }
        }
        
        // Ensure permissions is an array
        let permissions = user.permissions || [];
        if (typeof permissions === 'string') {
          try {
            permissions = JSON.parse(permissions);
          } catch (e) {
            permissions = [];
          }
        }

        // Merge dedicated columns back into the preferences shape so all
        // existing code that reads preferences.* still works during transition.
        const workingScheduleFromColumn = user.working_schedule ?? user.workingSchedule ?? null;
        const mergedPreferences = {
          ...(user.preferences || {}),
          ...(workingScheduleFromColumn ? { workingSchedule: workingScheduleFromColumn } : {}),
          // Keep backwards-compat aliases populated from dedicated columns
          ...(user.notifications_enabled !== undefined ? { notifications: user.notifications_enabled } : {}),
          ...(user.email_digest ? { emailDigest: user.email_digest } : {}),
        };

        transformedUsers[userId] = {
          ...user,
          assignedProjects,
          permissions,
          preferences: mergedPreferences,
          // Ensure camelCase and proper mapping
          companyId: user.companyId || user.company_id,
          firstName: user.firstName || user.first_name,
          lastName: user.lastName || user.last_name,
          displayName: user.displayName || user.display_name,
          passwordHash: user.passwordHash || user.password_hash,
          jobTitle: user.jobTitle || user.job_title,
          manualPresenceStatus: user.manualPresenceStatus || user.manual_presence_status,
          manualPresenceLabel: user.manualPresenceLabel || user.manual_presence_label,
          manualPresenceUntil: user.manualPresenceUntil || user.manual_presence_until,
          manualPresenceClearMode: user.manualPresenceClearMode || user.manual_presence_clear_mode,
          manualPresenceSetAt: user.manualPresenceSetAt || user.manual_presence_set_at,
          lastLogin: user.lastLogin || user.last_login,
          emergencyContact: user.emergencyContact || user.emergency_contact,
          emergencyContactName: user.emergencyContactName || user.emergency_contact_name,
          emergencyContactPhone: user.emergencyContactPhone || user.emergency_contact_phone,
          emergencyContactRelationship: user.emergencyContactRelationship || user.emergency_contact_relationship,
          timezone: user.timezone,
          parentUserId: user.parentUserId || user.parent_user_id,
          supervisorRole: user.supervisorRole || user.supervisor_role,
          oauthProvider: user.oauthProvider || user.oauth_provider,
          legacyId: user.legacyId || user.legacy_id,
          emailSignatures: normalizeEmailSignatures(user.emailSignatures ?? user.email_signatures),
        };
      });
      data.users = transformedUsers;
    }
    
    // Transform all tasks from backend format to frontend format
    if (data.tasks) {
      const transformedTasks: any = {};
      Object.keys(data.tasks).forEach(taskId => {
        transformedTasks[taskId] = this.transformSingleTaskFromBackend(data.tasks[taskId]);
      });
      data.tasks = transformedTasks;
    }

    if (data.projectPlannerItems) {
      const transformedPlannerItems: any = {};
      Object.keys(data.projectPlannerItems).forEach(itemId => {
        const item = data.projectPlannerItems[itemId];

        const toPlannerLinkType = (value: unknown): 'FS' | 'SS' | 'FF' | 'SF' => {
          const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
          if (normalized === 'SS' || normalized === 'FF' || normalized === 'SF') return normalized;
          return 'FS';
        };

        const parseMaybeJsonArray = (value: unknown): unknown[] => {
          if (typeof value === 'string') {
            try {
              const parsed = JSON.parse(value);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          }
          return Array.isArray(value) ? value : [];
        };

        const parsePredecessorLinkEntry = (value: unknown): { predecessorId: string; linkType: 'FS' | 'SS' | 'FF' | 'SF' } | null => {
          if (typeof value === 'string' || typeof value === 'number') {
            const predecessorId = String(value).trim();
            return predecessorId ? { predecessorId, linkType: 'FS' } : null;
          }
          if (!value || typeof value !== 'object') return null;

          const raw = value as Record<string, unknown>;
          const predecessorIdValue = raw.predecessorId ?? raw.predecessor_id ?? raw.id;
          const predecessorId = typeof predecessorIdValue === 'string' || typeof predecessorIdValue === 'number'
            ? String(predecessorIdValue).trim()
            : '';
          if (!predecessorId) return null;

          return {
            predecessorId,
            linkType: toPlannerLinkType(raw.linkType ?? raw.link_type ?? raw.type),
          };
        };

        const predecessorLinkById = new Map<string, 'FS' | 'SS' | 'FF' | 'SF'>();
        const predecessorCandidates = [
          ...parseMaybeJsonArray(item.predecessorLinks ?? item.predecessor_links),
          ...parseMaybeJsonArray(item.predecessorIds ?? item.predecessor_ids),
        ];
        predecessorCandidates.forEach(value => {
          const parsed = parsePredecessorLinkEntry(value);
          if (!parsed) return;
          const existing = predecessorLinkById.get(parsed.predecessorId);
          if (!existing || (existing === 'FS' && parsed.linkType !== 'FS')) {
            predecessorLinkById.set(parsed.predecessorId, parsed.linkType);
          }
        });
        const predecessorLinks = Array.from(predecessorLinkById.entries()).map(([predecessorId, linkType]) => ({
          predecessorId,
          linkType,
        }));
        const predecessorIds = predecessorLinks.map(link => link.predecessorId);

        let attachments = item.attachments || [];
        if (typeof attachments === 'string') {
          try {
            attachments = JSON.parse(attachments);
          } catch {
            attachments = [];
          }
        }

        const estimatedHoursValue = Number(item.estimatedHours ?? item.estimated_hours);
        const timeLogMinutesValue = Number(item.timeLogMinutes ?? item.time_log_minutes);
        const costValue = Number(item.cost);
        const actualCostValue = Number(item.actualCost ?? item.actual_cost);
        const rawDeadlineDate = item.deadlineDate || item.deadline_date;

        transformedPlannerItems[itemId] = {
          ...item,
          projectId: item.projectId || item.project_id,
          parentId: item.parentId || item.parent_id || undefined,
          itemType: item.itemType || item.item_type || 'task',
          ownerName: item.ownerName || item.owner_name || '',
          workPackage: item.workPackage || item.work_package || '',
          workZone: item.workZone || item.work_zone || '',
          wbsNumber: item.wbsNumber || item.wbs_number || '',
          predecessorIds,
          predecessorLinks,
          attachments: Array.isArray(attachments) ? attachments : [],
          startDate: (item.startDate || item.start_date || new Date().toISOString()).slice(0, 10),
          endDate: (item.endDate || item.end_date || item.startDate || item.start_date || new Date().toISOString()).slice(0, 10),
          deadlineDate: typeof rawDeadlineDate === 'string' && rawDeadlineDate
            ? rawDeadlineDate.slice(0, 10)
            : undefined,
          estimatedHours: Number.isFinite(estimatedHoursValue) ? estimatedHoursValue : undefined,
          timeLogMinutes: Number.isFinite(timeLogMinutesValue) ? timeLogMinutesValue : undefined,
          cost: Number.isFinite(costValue) ? costValue : undefined,
          actualCost: Number.isFinite(actualCostValue) ? actualCostValue : undefined,
          updatedBy: item.updatedBy || item.updated_by,
          createdBy: item.createdBy || item.created_by,
          notes: item.notes || '',
          priority: item.priority || 'medium',
          status: item.status || 'not_started',
          progress: typeof item.progress === 'number' ? item.progress : Number(item.progress || 0),
          displayOrder: typeof item.displayOrder === 'number'
            ? item.displayOrder
            : typeof item.display_order === 'number'
              ? item.display_order
              : Number.isFinite(Number(item.displayOrder ?? item.display_order))
                ? Number(item.displayOrder ?? item.display_order)
                : undefined,
        };
      });
      data.projectPlannerItems = transformedPlannerItems;
    }
    
    // Transform task comments from backend format to frontend format
    if (data.taskComments) {
      const transformedComments: any = {};
      Object.keys(data.taskComments).forEach(commentId => {
        const comment = data.taskComments[commentId];
        transformedComments[commentId] = {
          ...comment,
          // Map 'content' back to 'comment'
          comment: comment.content || comment.comment,
          // Ensure snake_case field names that frontend expects
          task_id: comment.taskId || comment.task_id,
          user_id: comment.userId || comment.user_id,
          user_name: comment.userName || comment.user_name,
          created_at: comment.createdAt || comment.created_at,
          visible_to_client: comment.visibleToClient !== undefined ? comment.visibleToClient : comment.visible_to_client,
        };
      });
      data.taskComments = transformedComments;
    }
    
    // Transform companies from backend format to frontend format
    if (data.companies) {
      const transformedCompanies: any = {};
      const parseCompanyAddress = (company: any) => {
        // DB columns are address_line1 / address_line2 (no trailing underscore).
        // The camelCase forms arrive from the single-fetch path; snake_case
        // with trailing underscore (address_line_1) does not exist in the DB.
        let addressLine1 = company.addressLine1 || company.address_line1 || company.address_line_1 || '';
        let addressLine2 = company.addressLine2 || company.address_line2 || company.address_line_2 || '';
        let town = company.town || '';
        let county = company.county || '';
        let postcode = company.postcode || '';

        const parseAddressObject = (candidate: unknown): boolean => {
          if (!candidate || typeof candidate !== 'object') return false;
          const addressObj = candidate as Record<string, unknown>;
          const nextLine1 = typeof addressObj.line1 === 'string' ? addressObj.line1.trim() : '';
          const nextLine2 = typeof addressObj.line2 === 'string' ? addressObj.line2.trim() : '';
          const nextLine3 = typeof addressObj.line3 === 'string' ? addressObj.line3.trim() : '';
          const nextTown = typeof addressObj.town === 'string' ? addressObj.town.trim() : '';
          const nextCounty = typeof addressObj.county === 'string' ? addressObj.county.trim() : '';
          const nextPostcode = typeof addressObj.postcode === 'string' ? addressObj.postcode.trim() : '';

          addressLine1 = nextLine1 || addressLine1;
          addressLine2 = nextLine2 || nextLine3 || addressLine2;
          town = nextTown || town;
          county = nextCounty || county;
          postcode = nextPostcode || postcode;
          return true;
        };

        const rawAddress = typeof company.address === 'string' ? company.address.trim() : '';
        if (rawAddress && (rawAddress.startsWith('{') || rawAddress.startsWith('"'))) {
          try {
            let parsed: unknown = JSON.parse(rawAddress);
            if (typeof parsed === 'string' && parsed.trim().startsWith('{')) {
              parsed = JSON.parse(parsed);
            }
            parseAddressObject(parsed);
          } catch {
            // Keep raw address as-is when parsing fails.
          }
        } else if (company.address && typeof company.address === 'object') {
          parseAddressObject(company.address);
        }

        const normalizedAddress = [
          addressLine1,
          addressLine2,
          town,
          county,
          postcode,
        ].filter((part): part is string => typeof part === 'string' && part.trim().length > 0).join(', ');

        // Prefer the raw legacy address string when it contains more content
        // than what the structured columns assembled. This handles the common
        // case where address_line1/2/town/county are NULL in the DB but the
        // legacy `address` column holds a full comma-separated string (e.g.
        // "70-72, Kirkgate Silsden, Keighley, West Yorkshire, BD20 0PA").
        // Without this guard, normalizedAddress = "BD20 0PA" (postcode only)
        // would be returned and the legacy full string would be discarded.
        const isPlainLegacy =
          rawAddress &&
          !rawAddress.startsWith('{') &&
          !rawAddress.startsWith('"') &&
          rawAddress.length > normalizedAddress.length;

        return {
          address: isPlainLegacy ? rawAddress : normalizedAddress || rawAddress || '',
          addressLine1,
          addressLine2,
          town,
          county,
          postcode,
        };
      };

      Object.keys(data.companies).forEach(companyId => {
        const company = data.companies[companyId];
        const parsedAddress = parseCompanyAddress(company);
        transformedCompanies[companyId] = {
          ...company,
          ...parsedAddress,
          dataClassification: company.dataClassification || company.data_classification || 'production',
          companyNumber: company.companyNumber || company.company_number || '',
          primaryContact: company.primaryContact || company.primary_contact || null,
          vatNumber: company.vatNumber || company.vat_number || '',
          // Map legacy billing tiers to new plan IDs
          billing_tier: this.mapLegacyBillingTier(
            company.billingTier || company.billing_tier || 'bronze'
          ),
          billing_frequency: company.billingFrequency || company.billing_frequency || 'monthly',
          // Parse subscription_price from string to number (PostgreSQL numeric returns as string)
          subscription_price: (company.subscriptionPrice || company.subscription_price)
            ? parseFloat(company.subscriptionPrice || company.subscription_price)
            : 0,
        };
      });
      data.companies = transformedCompanies;
    }

    const camelifyWorkspaceId = (entity: any): any => {
      if (!entity || typeof entity !== 'object') return entity;
      const wsId = entity.workspaceId ?? entity.workspace_id ?? null;
      if (wsId !== null && entity.workspaceId == null) {
        entity.workspaceId = wsId;
      }
      // client_id (snake_case from the DB) must also be surfaced as clientId
      // so the Project List lookup map can find the right Company row.
      if (entity.client_id != null && entity.clientId == null) {
        entity.clientId = entity.client_id;
      }
      return entity;
    };
    const camelifyAll = (collection: Record<string, any> | undefined) => {
      if (!collection) return;
      Object.keys(collection).forEach(k => camelifyWorkspaceId(collection[k]));
    };
    camelifyAll(data.projects);
    camelifyAll(data.users);
    camelifyAll(data.contractItems);
    camelifyAll(data.rateLibraries);

    // Companies have no workspace_id of their own — the link lives
    // on workspaces.legacy_company_id. Join here so Company carries
    // the id of its workspace mirror.
    if (data.companies && data.workspaces) {
      const companyToWorkspace: Record<string, string> = {};
      Object.values<any>(data.workspaces).forEach((w) => {
        if (w?.legacy_company_id) companyToWorkspace[String(w.legacy_company_id)] = String(w.id);
        if (w?.legacyCompanyId)   companyToWorkspace[String(w.legacyCompanyId)]   = String(w.id);
      });
      Object.values<any>(data.companies).forEach((c) => {
        if (!c) return;
        const wsId = companyToWorkspace[String(c.id)];
        if (wsId && c.workspaceId == null) c.workspaceId = wsId;
      });
    }

    if (data.activities) {
      Object.keys(data.activities).forEach(k => {
        const a = data.activities[k];
        if (!a) return;
        if (a.client_workspace_id  != null && a.clientWorkspaceId  == null) a.clientWorkspaceId  = a.client_workspace_id;
        if (a.company_workspace_id != null && a.companyWorkspaceId == null) a.companyWorkspaceId = a.company_workspace_id;
      });
    }
    
    // Validate and fix all project contractor references
    if (data.projects) {
      Object.keys(data.projects).forEach(projectId => {
        const project = data.projects[projectId];
        
        // Build valid contractor ID set for fast lookup
        const validContractorIds = new Set(
          data.mainContractors ? Object.keys(data.mainContractors) : []
        );
        
        // Check if mainContractorId is orphaned - silently fix
        if (project.mainContractorId && !validContractorIds.has(project.mainContractorId)) {
          project.mainContractorId = null;
          project.mainContractor = null;
        }
        
        // Check if mainContractor field contains an orphaned ID - silently fix
        if (project.mainContractor && typeof project.mainContractor === 'string') {
          const looksLikeId = /^\d{13}-[a-z0-9]+$/.test(project.mainContractor);
          if (looksLikeId && !validContractorIds.has(project.mainContractor)) {
            project.mainContractor = null;
          }
        }

        // Hydrate mainContractor Name from ID for frontend compatibility
        // Backend stores ID in main_contractor, but frontend expects Name in mainContractor
        if (project.mainContractorId && data.mainContractors && data.mainContractors[project.mainContractorId]) {
          project.mainContractor = data.mainContractors[project.mainContractorId].name;
        } else if (project.mainContractor && typeof project.mainContractor === 'string') {
           // If mainContractor holds an ID (because backend returned it), try to resolve to Name
           if (data.mainContractors && data.mainContractors[project.mainContractor]) {
              const id = project.mainContractor;
              project.mainContractorId = id;
              project.mainContractor = data.mainContractors[id].name;
           }
        }

        // Transform snake_case backend fields to camelCase for frontend
        if (project.resource_cost_category !== undefined) {
          project.resourceCostCategory = project.resource_cost_category;
        }
        if (project.internal_notes !== undefined) {
          project.internalNotes = project.internal_notes;
        }
        // Transform site_address from backend to siteAddress for frontend
        if (project.site_address !== undefined) {
          project.siteAddress = project.site_address;
        }
        
        // Transform tender dates from backend column names to frontend property names
        // Database: tender_issue_date, tender_submission_date, tender_due_date
        // Frontend: tenderIssueDate, tenderSubmissionDate, tenderDueDate
        if (project.tender_issue_date !== undefined) {
          project.tenderIssueDate = project.tender_issue_date;
        }
        if (project.tender_submission_date !== undefined) {
          project.tenderSubmissionDate = project.tender_submission_date;
          project.tenderReturnDate = project.tender_submission_date; // Also set legacy name
        }
        if (project.tender_due_date !== undefined) {
          project.tenderDueDate = project.tender_due_date;
        }
        
        // Transform project_stage from backend - this is the authoritative source
        // Backend and frontend now use the same enum values (no conversion needed)
        let finalStage: string;
        if (project.project_stage !== undefined && project.project_stage !== null) {
          finalStage = project.project_stage;
        } else if (project.projectStage !== undefined && project.projectStage !== null) {
          finalStage = project.projectStage;
        } else if (project.stage !== undefined && project.stage !== null) {
          finalStage = project.stage;
        } else {
          finalStage = 'tender_in_progress';  // Default to new enum value
        }
        
        // Set all stage-related fields to the value
        project.phase = finalStage;
        project.projectStage = finalStage;
        project.stage = finalStage;
        if (project.pipeline_status !== undefined) project.pipelineStatus = project.pipeline_status;

        // This loop is the real hydration path for every screen (unlike
        // mapBackendProjectToFrontend, which only backs the unused
        // fetchProject()) — the side/appointment/named-contact mapping has
        // to be applied here too, against this same raw row.
        Object.assign(project, projectRoleFieldsFromBackend(project));
      });
    }
    
    return data;
  }

  /**
   * Transform a single task from backend format to frontend format
   */
  protected transformSingleTaskFromBackend(task: any): any {
    if (!task) return task;
    
    // Reverse priority mapping: database 'critical' -> frontend 'urgent'
    // Database has: 'low', 'medium', 'high', 'critical'
    // Frontend uses: 'low', 'medium', 'high', 'urgent', 'critical'
    // We map database 'critical' to frontend 'urgent' for consistency
    const reversePriorityMap: { [key: string]: string } = {
      'low': 'low',
      'medium': 'medium',
      'high': 'high',
      'critical': 'urgent', // Map database critical back to frontend urgent
    };
    
    // Reverse status mapping
    const reverseStatusMap: { [key: string]: string } = {
      'pending': 'pending',
      'in-progress': 'in_progress', // Map hyphen back to underscore
      'awaiting-response': 'awaiting_response', // Map hyphen back to underscore
      'completed': 'completed',
      'cancelled': 'cancelled',
    };
    
    // Reverse type mapping: database simplified types -> frontend detailed types
    // Database has: 'deadline', 'followup', 'review', 'submission', 'approval', 'general'
    // Frontend uses: 'email', 'phone_call', 'chase', 'follow_up', 'measure', etc.
    // Since multiple frontend types map to the same backend type, we pick the most common one
    const reverseTypeMap: { [key: string]: string } = {
      'deadline': 'other',
      'followup': 'follow_up', // Default followup type
      'review': 'review',
      'submission': 'submission',
      'approval': 'other',
      'general': 'other', // Default general type
    };
    
    // Backend returns camelCase, frontend uses snake_case
    // Map the camelCase properties to snake_case
    const projectId = task.projectId || task.project_id;
    const createdById = task.createdBy || task.created_by;
    const assignedToId = task.assignedTo || task.assigned_to;
    const dueDate = task.dueDate || task.due_date;
    const sourceDetails = task.sourceDetails || task.source_details || '';
    
    // Extract original type from source_details if it was stored there
    let taskType = reverseTypeMap[task.type] || task.type;
    const originalTypeMatch = sourceDetails.match(/original_type:([^|]+)/);
    if (originalTypeMatch && originalTypeMatch[1]) {
      taskType = originalTypeMatch[1];
    }
    
    // Enrich task with project and client names (denormalized fields not in backend DB)
    const project = projectId ? this.db.projects[projectId] : null;
    const client = project?.clientId ? this.db.companies[project.clientId] : null;
    
    const createdBy = createdById ? this.db.users[createdById] : null;
    const assignedTo = assignedToId ? this.db.users[assignedToId] : null;
    
    // Clean source_details by removing the original_type marker before returning to frontend
    let cleanSourceDetails = sourceDetails.replace(/\|?original_type:[^|]+\|?/g, '').trim();
    if (cleanSourceDetails.startsWith('|')) cleanSourceDetails = cleanSourceDetails.slice(1);
    if (cleanSourceDetails.endsWith('|')) cleanSourceDetails = cleanSourceDetails.slice(0, -1);
    
    return {
      ...task,
      // Normalize to snake_case for frontend
      project_id: projectId,
      created_by: createdById,
      assigned_to: assignedToId,
      due_date: dueDate,
      priority: reversePriorityMap[task.priority] || task.priority,
      status: reverseStatusMap[task.status] || task.status,
      type: taskType, // Use the extracted original type
      source_details: cleanSourceDetails || undefined,
      project_name: project?.name || '',
      client_company_id: project?.clientId || '',
      client_name: client?.name || '',
      // The local users map wins (it is the live row), but an actor outside the
      // caller's workspace is never in it — the server sends just their display
      // name alongside the task for exactly that case.
      created_by_name: createdBy?.name || task.createdByName || task.created_by_name || '',
      assigned_to_name: assignedTo?.name || task.assignedToName || task.assigned_to_name || '',
    };
  }

  /**
   * Helper to map legacy billing tiers to new plan IDs
   */
  protected mapLegacyBillingTier(tier: string): 'bronze' | 'silver' | 'gold' | 'platinum' {
    if (!tier) return 'bronze';
    
    const lowerTier = tier.toLowerCase();
    
    // Direct matches
    if (['bronze', 'silver', 'gold', 'platinum'].includes(lowerTier)) {
      return lowerTier as any;
    }
    
    // Legacy mapping
    if (lowerTier === 'essential') return 'silver'; // £250/m
    if (lowerTier === 'professional') return 'gold'; // £750/m
    if (lowerTier === 'enterprise') return 'platinum'; // £1100/w
    
    // Default
    return 'bronze';
  }

  /**
   * Helper method to delete entity from backend immediately
   */
  protected async deleteEntityFromBackend(collection: string, id: string): Promise<void> {
    if (!this.useSupabase) return;

    try {
      const token = await this.getAuthHeaderToken();

      const response = await fetch(`${API_BASE}/${collection}/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        console.error(`❌ Backend DELETE ${collection} failed:`, response.status, await response.text());
      } else {
        this.supabaseRetryCount = 0;
        this.supabaseDisabledReason = null;
      }
    } catch (error) {
      console.error(`❌ Error deleting ${collection} from backend:`, error);
      const message = typeof (error as any)?.message === 'string' ? (error as any).message : '';
      if (message.includes('connection closed') || message.includes('Failed to fetch') || (error as any)?.name === 'AbortError') {
        this.supabaseRetryCount += 1;
        const attempts = `${this.supabaseRetryCount}/${this.MAX_SUPABASE_RETRIES}`;
        console.warn(`⚠️ Supabase delete failed (attempt ${attempts}): ${message}`);
        if (this.supabaseRetryCount >= this.MAX_SUPABASE_RETRIES) {
          this.disableSupabase(message || 'network error');
        }
      }
      // Don't throw - localStorage is already updated
    }
  }

  protected transformCompanyForBackend(company: any): any {
    const name = typeof company.name === 'string' ? company.name.trim() : '';
    if (!name) {
      throw new Error('Company name is required before saving to backend');
    }

    // Ensure type is always included and valid
    const companyData: any = {
      id: company.id,
      name,
      type: company.type || 'client', // Default to 'client' if missing
      email: company.email,
      status: company.status || 'active',
      ...(company.suspension_reason !== undefined || company.suspensionReason !== undefined ? { suspension_reason: company.suspension_reason ?? company.suspensionReason } : {}),
      created_at: company.createdAt,
      updated_at: company.updatedAt,
    };

    // Include optional fields that exist in the database (using snake_case for backend)
    if (company.phone) companyData.phone = company.phone;
    if (company.address) companyData.address = company.address;
    if (company.addressLine1 !== undefined) companyData.address_line1 = company.addressLine1 || null;
    if (company.addressLine2 !== undefined) companyData.address_line2 = company.addressLine2 || null;
    if (company.town !== undefined)         companyData.town            = company.town || null;
    if (company.county !== undefined)       companyData.county          = company.county || null;
    if (company.postcode) companyData.postcode = company.postcode;
    if (company.logo) companyData.logo = company.logo;
    if (company.companyNumber) companyData.company_number = company.companyNumber;
    if (company.website) companyData.website = company.website;
    // Map frontend tier names (essential/professional/enterprise) to DB-accepted
    // legacy values (bronze/silver/gold/platinum) required by companies_billing_tier_check.
    if (company.billing_tier) companyData.billing_tier = this.mapLegacyBillingTier(company.billing_tier);
    if (company.billing_frequency) companyData.billing_frequency = company.billing_frequency;
    if (company.subscription_price !== undefined) companyData.subscription_price = company.subscription_price;
    if (company.settings) companyData.settings = company.settings;
    if (company.primaryContact) companyData.primary_contact = company.primaryContact;
    if (company.notes) companyData.notes = company.notes;
    if (company.createdBy) companyData.created_by = company.createdBy;
    
    // Location fields for mapping
    if (company.latitude !== undefined) companyData.latitude = company.latitude;
    if (company.longitude !== undefined) companyData.longitude = company.longitude;
    
    // Subscription & contract fields
    if (company.contract_term) companyData.contract_term = company.contract_term;
    if (company.subscription_start_date) companyData.subscription_start_date = company.subscription_start_date;
    if (company.subscription_end_date) companyData.subscription_end_date = company.subscription_end_date;
    if (company.cooling_off_end_date) companyData.cooling_off_end_date = company.cooling_off_end_date;
    if (company.notice_served_date) companyData.notice_served_date = company.notice_served_date;

    // Current billing period (renewal tracking)
    if (company.subscription_period_start) companyData.subscription_period_start = company.subscription_period_start;
    if (company.subscription_period_end)   companyData.subscription_period_end   = company.subscription_period_end;
    if (company.subscription_term_months !== undefined && company.subscription_term_months !== null) {
      companyData.subscription_term_months = company.subscription_term_months;
    }
    if (company.subscription_source) companyData.subscription_source = company.subscription_source;

    // Xero integration fields
    if (company.xeroContactId) companyData.xero_contact_id = company.xeroContactId;
    if (company.xeroTenantId) companyData.xero_tenant_id = company.xeroTenantId;
    if (company.xeroConnectedAt) companyData.xero_connected_at = company.xeroConnectedAt;

    if (company.organisation_type !== undefined) companyData.organisation_type = company.organisation_type;
    else if (company.organisationType !== undefined) companyData.organisation_type = company.organisationType;
    if (company.trade     !== undefined) companyData.trade     = company.trade;
    if (company.vat       !== undefined) companyData.vat       = company.vat;
    if (company.cis       !== undefined) companyData.cis       = company.cis;
    if (company.bank      !== undefined) companyData.bank      = company.bank;
    if (company.invoicing !== undefined) companyData.invoicing = company.invoicing;

    return companyData;
  }

  protected transformNotificationForBackend(notification: any): any {
    // Map priority values to match database enum constraint
    // Database expects: 'low', 'normal', 'high', 'urgent'
    const priorityMap: { [key: string]: string } = {
      'low': 'low',
      'medium': 'normal', // Map medium to normal for database
      'normal': 'normal',
      'high': 'high',
      'critical': 'urgent', // Map critical to urgent for database
      'urgent': 'urgent',
    };

    const notificationData: any = {
      id: notification.id,
      user_id: notification.user_id || notification.userId,
      type: notification.type,
      priority: priorityMap[notification.priority] || 'normal', // Default to 'normal'
      title: notification.title,
      message: notification.message,
      read: notification.read || false,
      created_at: notification.timestamp || notification.createdAt || notification.created_at,
    };

    // Optional fields (only include fields that exist in the database schema)
    if (notification.actionUrl || notification.action_url) notificationData.action_url = notification.action_url || notification.actionUrl;
    if (notification.actionLabel || notification.action_label) notificationData.action_label = notification.action_label || notification.actionLabel;
    if (notification.relatedEntityType || notification.related_entity_type) notificationData.related_entity_type = notification.related_entity_type || notification.relatedEntityType;
    if (notification.relatedEntityId || notification.related_entity_id) notificationData.related_entity_id = notification.related_entity_id || notification.relatedEntityId;
    if (notification.readAt || notification.read_at) notificationData.read_at = notification.read_at || notification.readAt;
    
    // Store project info and company info in metadata since they're not direct columns in the database
    const metadata: any = notification.metadata ? { ...notification.metadata } : {};
    if (notification.projectId || notification.project_id) metadata.projectId = notification.project_id || notification.projectId;
    if (notification.projectName || notification.project_name) metadata.projectName = notification.project_name || notification.projectName;
    if (notification.companyId || notification.company_id) metadata.companyId = notification.company_id || notification.companyId;
    
    // Only set metadata if it has any content
    if (Object.keys(metadata).length > 0) {
      notificationData.metadata = metadata;
    }

    return notificationData;
  }

  protected transformActivityForBackend(activity: any): any {
    const rawType = activity.type || '';
    const typeMap: Record<string, string> = {
      phoneCall: 'phone_call',
      phone_call: 'phone_call',
      meeting: 'meeting',
      email: 'email',
      note: 'note',
      comment: 'comment',
      fileShare: 'file_share',
      file_share: 'file_share',
      statusUpdate: 'status_update',
      status_update: 'status_update',
      projectUpdate: 'project_update',
      project_update: 'project_update',
      contractValueChange: 'contract_value_change',
      contract_value_change: 'contract_value_change',
      interimApplication: 'interim_application',
      interim_application: 'interim_application',
      contractItem: 'contract_item',
      contract_item: 'contract_item',
      taskCreated: 'task_created',
      task_created: 'task_created',
      projectCreated: 'project_created',
      project_created: 'project_created',
      documentUpload: 'document_upload',
      document_upload: 'document_upload',
      documentDelete: 'document_delete',
      document_delete: 'document_delete',
      project_status_changed: 'status_update',
      systemUpdate: 'project_update',
      system_update: 'project_update',
    };
    const allowedTypes = new Set([
      'comment',
      'phone_call',
      'meeting',
      'email',
      'note',
      'file_share',
      'status_update',
      'document_upload',
      'document_delete',
      'project_update',
      'contract_value_change',
      'interim_application',
      'contract_item',
      'task_created',
      'project_created',
    ]);
    const mappedType = typeMap[rawType] || rawType;
    const normalizedType = allowedTypes.has(mappedType) ? mappedType : 'note';

    const relatedToType = activity.related_to_type || 'project';
    const relatedToId = activity.related_to_id || activity.project_id || 'unknown';

    const activityData: any = {
      id: activity.id,
      type: normalizedType,
      title: activity.title || '',
      description: activity.description || '',
      created_at: activity.created_at,
      related_to_type: relatedToType,
      related_to_id: relatedToId,
      related_to_name: activity.related_to_name || 'Unknown',
      created_by_id: activity.created_by_id || activity.user_id || 'system',
      created_by_name: activity.created_by_name || activity.user_name || 'System',
      created_by_email: activity.created_by_email || activity.user_email || '',
      // Engagement
      mentions: activity.mentions || [],
      replies_count: activity.replies_count || 0,
      parent_activity_id: activity.parent_activity_id || null,
      // Flags
      is_internal: activity.is_internal ?? true,
      is_pinned: activity.is_pinned ?? false,
      requires_follow_up: activity.requires_follow_up ?? false,
      follow_up_date: activity.follow_up_date || null,
      follow_up_assigned_to: activity.follow_up_assigned_to || null,
      is_resolved: activity.is_resolved ?? false,
      // Status / stage change fields
      status_from: activity.status_from || null,
      status_to: activity.status_to || null,
      // Document fields
      document_id: activity.document_id || null,
      document_name: activity.document_name || null,
      document_size: activity.document_size || null,
      document_category: activity.document_category || null,
      // Project change fields
      change_type: activity.change_type || null,
      change_field: activity.change_field || null,
      change_old_value: activity.change_old_value || null,
      change_new_value: activity.change_new_value || null,
      // Financial fields
      amount: activity.amount ?? null,
      currency: activity.currency || 'GBP',
      // Phone call fields
      call_duration_minutes: activity.call_duration_minutes ?? null,
      call_contact_name: activity.call_contact_name || null,
      call_contact_phone: activity.call_contact_phone || null,
      call_outcome: activity.call_outcome || null,
      // Meeting fields
      meeting_start_time: activity.meeting_start_time || null,
      meeting_end_time: activity.meeting_end_time || null,
      meeting_location: activity.meeting_location || null,
      meeting_attendees: activity.meeting_attendees || null,
      meeting_type: activity.meeting_type || null,
      // Email fields
      email_to: activity.email_to || null,
      email_cc: activity.email_cc || null,
      email_subject: activity.email_subject || null,
      email_sent_at: activity.email_sent_at || null,
      // File fields
      file_url: activity.file_url || null,
      file_name: activity.file_name || null,
      file_type: activity.file_type || null,
      file_size: activity.file_size ?? null,
    };

    // Set project_id / client_id from related_to when safe to do so
    if (relatedToType === 'project') activityData.project_id = relatedToId;
    if (relatedToType === 'client') activityData.client_id = relatedToId;

    // Belt-and-braces: ensure NOT NULL columns kind + source are always populated.
    // Mirrors the backend applyFieldTransformations logic so the row is valid
    // even if the backend backfill is skipped for any reason.
    const CRM_TYPES = new Set(['comment', 'phone_call', 'meeting', 'email', 'note', 'file_share']);
    const kind: 'agent' | 'crm' | 'system' =
      activity.kind
        ?? (activity.agent_id || activity.agentId ? 'agent'
          : CRM_TYPES.has(normalizedType) ? 'crm'
          : 'system');
    const source: 'human' | 'agent' | 'system' =
      activity.source
        ?? (kind === 'crm' ? 'human' : kind === 'agent' ? 'agent' : 'system');
    activityData.kind = kind;
    activityData.source = source;

    return activityData;
  }

  /**
   * Helper method to map raw backend project data to frontend Project interface
   */
  protected mapBackendProjectToFrontend(backendData: any): Project {
    // Resolve Stage (mimic transformDataFromBackend logic)
    let finalStage: string;
    if (backendData.project_stage !== undefined && backendData.project_stage !== null) {
      finalStage = backendData.project_stage;
    } else if (backendData.projectStage !== undefined && backendData.projectStage !== null) {
      finalStage = backendData.projectStage;
    } else if (backendData.stage !== undefined && backendData.stage !== null) {
      finalStage = backendData.stage;
    } else {
      finalStage = 'tender_in_progress';
    }

    // Resolve Main Contractor Name
    let mainContractorName = backendData.mainContractor || backendData.main_contractor || '';
    const mainContractorId = backendData.mainContractorId || backendData.main_contractor_id || undefined;
    
    // If mainContractor field holds an ID (which it does in the backend), or if we have an ID, look up the name
    if (mainContractorId) {
       const contractor = this.getMainContractor(mainContractorId);
       if (contractor) mainContractorName = contractor.name;
    } else if (mainContractorName && /^\d{13}-[a-z0-9]+$/.test(mainContractorName)) {
       // It looks like an ID
       const contractor = this.getMainContractor(mainContractorName);
       if (contractor) {
         mainContractorName = contractor.name;
       }
    }

    return {
      id: backendData.id,
      name: backendData.name,
      // project_number is now an auto-incrementing integer (1001+) — convert to string for display
      projectNumber: backendData.project_number !== undefined && backendData.project_number !== null
        ? String(backendData.project_number)
        : backendData.projectNumber || '',
      clientId: backendData.clientId || backendData.client_id || '',
      stage: finalStage as any, // Use resolved stage without conversion
      pipelineStatus: backendData.pipeline_status !== undefined ? backendData.pipeline_status : backendData.pipelineStatus,

      // Map other fields
      contractNumber: backendData.contractNumber || backendData.contract_number || '',
      mainContractorId: mainContractorId,
      mainContractor: mainContractorName,
      siteAddress: backendData.siteAddress || backendData.site_address || '',
      siteAddressLine1: backendData.siteAddressLine1 || backendData.site_address_line1 || '',
      siteAddressLine2: backendData.siteAddressLine2 || backendData.site_address_line2 || '',
      siteTown: backendData.siteTown || backendData.site_town || '',
      siteCounty: backendData.siteCounty || backendData.site_county || '',
      sitePostcode: backendData.sitePostcode || backendData.site_postcode || '',
      notes: backendData.notes || '',
      internalNotes: backendData.internalNotes || backendData.internal_notes || '',
      
      startDate: backendData.startDate || backendData.start_date,
      endDate: backendData.endDate || backendData.end_date,
      defectsLiabilityEndDate: backendData.defectsLiabilityEndDate || backendData.defects_liability_end_date,
      createdAt: backendData.createdAt || backendData.created_at,
      updatedAt: backendData.updatedAt || backendData.updated_at,
      createdBy: backendData.createdBy || backendData.created_by || '',
      
      contractValue: backendData.contractValue ? Number(backendData.contractValue) : 0,
      originalContractValue: backendData.originalContractValue ? Number(backendData.originalContractValue) : Number(backendData.original_contract_value || 0),
      revisedContractValue: backendData.revisedContractValue ? Number(backendData.revisedContractValue) : Number(backendData.revised_contract_value || 0),
      certifiedToDate: backendData.certifiedToDate ? Number(backendData.certifiedToDate) : Number(backendData.certified_to_date || 0),
      paidToDate: backendData.paidToDate ? Number(backendData.paidToDate) : Number(backendData.paid_to_date || 0),
      // Snake-case first for the contract-terms block: the server returns raw
      // rows, and a 2026-08-11 hotfix left dead camelCase twin COLUMNS
      // (always at their defaults) on projects - camel-first here read those
      // zeros and the admin form's saved values "reverted" on reload (#756).
      // Snake-first is correct with or without the twins present.
      retentionPercentage: Number(backendData.retention_percentage ?? backendData.retentionPercentage ?? 0),
      paymentTerms: backendData.payment_terms ?? backendData.paymentTerms ?? '0',
      contractType: backendData.contractType || backendData.contract_type || '',
      retentionLimit: (backendData.retention_limit ?? backendData.retentionLimit) != null
        ? Number(backendData.retention_limit ?? backendData.retentionLimit)
        : undefined,
      payLessNoticeDays: Number(backendData.pay_less_notice_days ?? backendData.payLessNoticeDays ?? 5),
      resourceCostCategory: backendData.resourceCostCategory || backendData.resource_cost_category || '',

      practicalCompletionDate: backendData.practicalCompletionDate || backendData.practical_completion_date,
      tenderIssueDate: backendData.tenderIssueDate || backendData.tender_issue_date,
      tenderSubmissionDate: backendData.tenderSubmissionDate || backendData.tender_submission_date,
      tenderDueDate: backendData.tenderDueDate || backendData.tender_due_date,

      // Tender queue fields. Nullish checks, not ||: 0 is a legal
      // queue_position and must survive the mapping.
      effortDays: backendData.effortDays ?? backendData.effort_days ?? undefined,
      queuePosition: backendData.queuePosition ?? backendData.queue_position ?? undefined,
      promisedReturnDate: backendData.promisedReturnDate || backendData.promised_return_date || undefined,
      
      // Arrays
      assignedStaffIds: backendData.assignedStaffIds || backendData.assigned_staff_ids || [],
      mainContractorContactIds: backendData.mainContractorContactIds || backendData.main_contractor_contact_ids || [],
      assignedSupervisorIds: backendData.assignedSupervisorIds || backendData.assigned_supervisor_ids || [],
      
      // Legacy/Other
      phase: finalStage,
      projectStage: finalStage,
      contractorName: backendData.contractorName || backendData.contractor_name || '',
      clientName: backendData.clientName || backendData.client_name || '',
      latitude: backendData.latitude,
      longitude: backendData.longitude,

      // Ensure these are preserved if they existed
      settings: backendData.settings || {},

      ...projectRoleFieldsFromBackend(backendData),
    } as any;
  }

  protected transformProjectForBackend(project: any): any {
    // Validate and fix main contractor reference
    let mainContractorName: string | null = null;
    let mainContractorId: string | null = null;
    
    // If mainContractorId is provided, validate it exists
    if (project.mainContractorId) {
      const contractor = this.getMainContractor(project.mainContractorId);
      if (contractor) {
        // Valid ID - use the name from the contractor record
        mainContractorId = contractor.id;
        mainContractorName = contractor.name;
      } else {
        // Invalid/orphaned ID - automatically clean it (silently unless in dev mode)
        mainContractorId = null;
        mainContractorName = null;
      }
    }
    
    // If mainContractor field is provided (string), validate it
    if (!mainContractorId && project.mainContractor && typeof project.mainContractor === 'string') {
      // Check if it looks like an ID (format: 1234567890123-abc123xyz)
      const looksLikeId = /^\d{13}-[a-z0-9]+$/.test(project.mainContractor);
      
      if (looksLikeId) {
        // It's an ID in the name field - try to find the contractor
        const contractor = this.getMainContractor(project.mainContractor);
        if (contractor) {
          mainContractorId = contractor.id;
          mainContractorName = contractor.name;
        } else {
          // Orphaned ID in name field - automatically clean it
          mainContractorName = null;
        }
      } else {
        // It's a contractor name - try to find the ID
        const contractors = this.getMainContractors();
        const contractor = contractors.find(c => c.name === project.mainContractor);
        if (contractor) {
          mainContractorId = contractor.id;
          mainContractorName = contractor.name;
        } else {
          // Unknown contractor name - we CANNOT send this to backend as it violates FK constraint
          // If we send a name that doesn't exist in main_contractors table, the update will fail with 500
          console.warn(`⚠️ [Data Integrity] Clearing invalid main contractor "${project.mainContractor}" for project "${project.name}" to prevent backend 500 error`);
          mainContractorName = null;
        }
      }
    }
    
    // The client relationship is authoritative. Prefer its mapped workspace
    // over an explicit project value so a legacy mismatched row cannot carry
    // its former tenant into the next write.
    const resolveWorkspaceId = (clientId: string | undefined): string | undefined => {
      return resolveProjectWorkspaceId(
        clientId,
        this.db.workspaces,
        project.workspaceId || project.workspace_id,
      );
    };

    // Transform project data to match backend schema
    const projectData: any = {
      id: project.id,
      name: project.name,
      client_id: project.clientId,
      workspace_id: resolveWorkspaceId(project.clientId),
      // Normalise to a DB-allowed canonical stage. The CHECK constraint
      // only accepts the canonical 12-value list; legacy aliases like
      // 'enquiry_received', 'tender_in_progress', 'client_review' come
      // in from forms / fixtures / leads promotion and would otherwise
      // 500 the POST. `normalizeProjectStage` maps them all and falls
      // back to 'tendering' for empty input.
      project_stage: normalizeProjectStage(project.project_stage || project.projectStage || project.stage),
      createdAt: project.createdAt,
      updatedAt: project.updatedAt,
      // Validated contractor fields
      main_contractor_id: mainContractorId,  // Contractor ID (reference) - validated or null
      // project_number is auto-generated by DB sequence (integer 1001+) — never write it from frontend
      service_type: project.serviceType || 'full-qs',
      contract_value: project.contractValue || project.value || 0,
      original_contract_value: project.originalContractValue || project.contractValue || project.value || 0,
      revised_contract_value: project.revisedContractValue || project.contractValue || project.value || 0,
      certified_to_date: project.certifiedToDate || 0,
      paid_to_date: project.paidToDate || 0,
      retention_percentage: project.retentionPercentage !== undefined ? project.retentionPercentage : 0,
      payment_terms: project.paymentTerms || 0,
    };

    // Legacy full saves must not erase an independent sales status they never
    // loaded. Include an explicit value (including null), never invent one.
    if (project.pipelineStatus !== undefined) projectData.pipeline_status = project.pipelineStatus;
    else if (project.pipeline_status !== undefined) projectData.pipeline_status = project.pipeline_status;

    // CRITICAL FIX: If mainContractorName is invalid (null), we MUST provide a fallback.
    // 1. Omitting the key keeps the existing invalid value -> FK Violation.
    // 2. Sending null -> Not-Null Violation.
    // 3. Sending the invalid name -> FK Violation.
    // Solution: Fallback to the first valid contractor in the list.
    
    if (!mainContractorName) {
       const allContractors = this.getMainContractors();
       if (allContractors.length > 0) {
         // Prefer a generic one if available
         const fallback = allContractors.find(c => c.name === 'Unknown' || c.name === 'TBC' || c.name === 'General') || allContractors[0];
         console.warn(`⚠️ [Data Integrity] Reassigning project "${project.name}" from invalid contractor "${project.mainContractor}" to "${fallback.name}" to satisfy DB constraints.`);
         mainContractorName = fallback.name;
         mainContractorId = fallback.id;
         
         // Append note about reassignment to description (since notes might not be on project)
         if (projectData.description) {
            projectData.description += `\n[System] Reassigned from invalid contractor "${project.mainContractor}" to "${fallback.name}".`;
         }
       }
    }

    projectData.main_contractor = mainContractorId;
    projectData.main_contractor_id = mainContractorId;

    // Add all other project fields
    if (project.type) projectData.type = project.type;
    if (project.description) projectData.description = project.description;
    if (project.startDate) projectData.start_date = project.startDate;
    if (project.endDate) projectData.end_date = project.endDate;
    // Map tender dates to correct database column names
    // Database columns: tender_issue_date, tender_submission_date, tender_due_date
    // Handle both camelCase and snake_case, and explicitly set to null if undefined
    if (project.tenderIssueDate !== undefined) {
      projectData.tender_issue_date = project.tenderIssueDate;
    } else if (project.tender_issue_date !== undefined) {
      projectData.tender_issue_date = project.tender_issue_date;
    } else if (project.tenderDate !== undefined) {
      projectData.tender_issue_date = project.tenderDate;
    }
    
    if (project.tenderSubmissionDate !== undefined) {
      projectData.tender_submission_date = project.tenderSubmissionDate;
    } else if (project.tender_submission_date !== undefined) {
      projectData.tender_submission_date = project.tender_submission_date;
    } else if (project.tenderReturnDate !== undefined) {
      // Legacy support for old field name
      projectData.tender_submission_date = project.tenderReturnDate;
    }
    
    if (project.tenderDueDate !== undefined) {
      projectData.tender_due_date = project.tenderDueDate;
    } else if (project.tender_due_date !== undefined) {
      projectData.tender_due_date = project.tender_due_date;
    }

    // Tender queue fields — null clears the column (unsize / remove promise).
    if (project.effortDays !== undefined) projectData.effort_days = project.effortDays;
    if (project.queuePosition !== undefined) projectData.queue_position = project.queuePosition;
    if (project.promisedReturnDate !== undefined) projectData.promised_return_date = project.promisedReturnDate;
    // `location` is a legacy single-line address column — no longer written.
    // Structured fields (site_address_line1, site_town, site_postcode, etc.) below carry the address.
    if (project.siteAddress) projectData.site_address = project.siteAddress;
    if (project.latitude) projectData.latitude = project.latitude;
    if (project.longitude) projectData.longitude = project.longitude;
    if (project.primaryContact) projectData.primary_contact = project.primaryContact;
    if (project.createdBy) projectData.created_by = project.createdBy;
    if (project.contractType) projectData.contract_type = project.contractType;
    if (project.contractNumber) projectData.contract_number = project.contractNumber;
    if (project.retentionLimit) projectData.retention_limit = project.retentionLimit;
    // Loosely typed on Project (the admin form casts), so read it the same way.
    if ((project as any).payLessNoticeDays !== undefined) {
      projectData.pay_less_notice_days = (project as any).payLessNoticeDays;
    }
    Object.assign(projectData, projectRoleFieldsForBackend(project));
    if (project.practicalCompletionDate) projectData.practical_completion_date = project.practicalCompletionDate;
    if (project.defectsLiabilityEndDate) projectData.defects_liability_end_date = project.defectsLiabilityEndDate;
    if (project.originalContractValue !== undefined) projectData.original_contract_value = project.originalContractValue;
    if (project.revisedContractValue !== undefined) projectData.revised_contract_value = project.revisedContractValue;
    if (project.certifiedToDate !== undefined) projectData.certified_to_date = project.certifiedToDate;
    if (project.settings !== undefined) projectData.settings = project.settings;
    if (project.paidToDate !== undefined) projectData.paid_to_date = project.paidToDate;

    // Team assignments
    if (project.mainContractorContactIds !== undefined) {
      projectData.main_contractor_contact_ids = project.mainContractorContactIds;
    }
    if (project.assignedSupervisorIds !== undefined) {
      projectData.assigned_supervisor_ids = project.assignedSupervisorIds;
    }
    if (project.assignedStaffIds !== undefined) {
      projectData.assigned_staff_ids = project.assignedStaffIds;
    }
    if (project.projectManager) projectData.project_manager = project.projectManager;
    if (project.quantitySurveyor) projectData.quantity_surveyor = project.quantitySurveyor;
    if (project.assignedQs) projectData.assigned_qs = project.assignedQs;
    
    // Resource cost category and internal notes
    if (project.resourceCostCategory !== undefined) projectData.resource_cost_category = project.resourceCostCategory;
    if (project.internalNotes !== undefined) projectData.internal_notes = project.internalNotes;

    // UK Address fields - structured address components
    if (project.siteAddressLine1 !== undefined) projectData.site_address_line1 = project.siteAddressLine1;
    if (project.siteAddressLine2 !== undefined) projectData.site_address_line2 = project.siteAddressLine2;
    if (project.siteTown !== undefined) projectData.site_town = project.siteTown;
    if (project.siteCounty !== undefined) projectData.site_county = project.siteCounty;
    if (project.sitePostcode !== undefined) projectData.site_postcode = project.sitePostcode;
    if (project.notes !== undefined) projectData.notes = project.notes;

    return projectData;
  }

  protected transformUserForBackend(user: any): any {
    // Handle both snake_case (company_id) and camelCase (companyId) for backwards compatibility
    const companyId = user.company_id || user.companyId;
    
    // Validate company_id is not empty
    if (!companyId) {
      console.error('❌ User creation error: company_id is required but was:', companyId, 'User data:', user);
      throw new Error('company_id is required for user creation');
    }

    // CRITICAL: Validate password_hash exists
    const passwordHash = user.passwordHash || user.password_hash;
    
    // NOTE: We no longer throw an error if password_hash is missing, 
    // because Supabase Auth handles passwords and updates might not include it.
    if (!passwordHash) {
    }

    const explicitWorkspaceId = user.workspaceId || user.workspace_id;
    let workspaceId: string | undefined = explicitWorkspaceId;
    if (!workspaceId && companyId) {
      const workspaces = this.db.workspaces || {};
      for (const ws of Object.values<any>(workspaces)) {
        if (ws?.legacy_company_id === companyId || ws?.legacyCompanyId === companyId) {
          workspaceId = ws.id;
          break;
        }
      }
    }

    // Transform user data to match backend schema
    const userData: any = {
      id: user.id,
      email: user.email,
      name: user.name,
      role: user.role,
      company_id: companyId,
      workspace_id: workspaceId,
      active_workspace_id: user.activeWorkspaceId ?? user.active_workspace_id ?? workspaceId,
      created_at: user.created_at || user.createdAt,
      updated_at: user.updated_at || user.updatedAt,
    };

    // Only include password_hash if it exists
    if (passwordHash) {
      userData.password_hash = passwordHash;
    }

    // Optional fields
    if (user.avatar) userData.avatar = user.avatar;
    // Gate on key-presence (not truthiness) so clearing phone / job title
    // persists as an explicit null rather than being dropped from the PUT —
    // otherwise a blanked field silently reappears on the next backend read.
    if ('phone' in user) userData.phone = user.phone || null;
    if ('firstName' in user || 'first_name' in user) userData.first_name = user.firstName ?? user.first_name ?? null;
    if ('lastName' in user || 'last_name' in user) userData.last_name = user.lastName ?? user.last_name ?? null;
    if ('displayName' in user || 'display_name' in user) userData.display_name = user.displayName ?? user.display_name ?? null;
    if ('position' in user || 'jobTitle' in user) userData.job_title = user.position || user.jobTitle || null;
    if ('department' in user) userData.department = user.department || null;
    if (user.status) userData.status = user.status;
    if (user.permissions) userData.permissions = user.permissions;
    if (user.preferences) {
      // Strip the field variants that DO live in dedicated columns / tables.
      // Both camelCase (from in-memory) and snake_case (legacy DB residue).
      //
      // `manualPresence` is deliberately NOT in this list. It used to be, on the
      // premise that it "now lives in a dedicated column" — it does not. The
      // manual_presence_* columns below are written only from a TOP-LEVEL
      // user.manualPresenceStatus, and no presence caller sets that: all three
      // writers (ProfileOverviewPanel, AppSidebar, NativePresenceBridge) pass
      // the NESTED preferences.manualPresence. So the value was destructured
      // out here and then dropped on the floor — note how workingSchedule two
      // lines down is rescued into its own column and this never was.
      //
      // The effect was total: every status change PUT `{ theme }` and nothing
      // else, so no device ever stored a status and no other device could read
      // one. It looked like a sync bug; the value simply never left the browser.
      const {
        notifications: _n,
        emailDigest: _e,
        email_digest: _e2,
        messageWorkflowStates: _m,
        message_workflow_states: _m2,
        workingSchedule: _ws,
        working_schedule: _ws2,
        ...themeOnly
      } = user.preferences;
      userData.preferences = themeOnly;
      // Dedicated working_schedule column (camelCase wins over snake_case)
      const ws = _ws ?? _ws2;
      if (ws) userData.working_schedule = ws;
    }
    // Allow working_schedule to be passed directly (bypasses preferences)
    if (user.working_schedule) userData.working_schedule = user.working_schedule;
    // notifications_enabled column
    if ('notifications_enabled' in user) userData.notifications_enabled = user.notifications_enabled;
    else if (user.preferences?.notifications !== undefined) userData.notifications_enabled = user.preferences.notifications;
    // email_digest column
    if ('email_digest' in user) userData.email_digest = user.email_digest;
    else if (user.preferences?.emailDigest) userData.email_digest = user.preferences.emailDigest;
    // email_signatures column
    if ('emailSignatures' in user) userData.email_signatures = user.emailSignatures;
    if ('manualPresenceStatus' in user || 'manual_presence_status' in user) userData.manual_presence_status = user.manualPresenceStatus ?? user.manual_presence_status ?? null;
    if ('manualPresenceLabel' in user || 'manual_presence_label' in user) userData.manual_presence_label = user.manualPresenceLabel ?? user.manual_presence_label ?? null;
    if ('manualPresenceUntil' in user || 'manual_presence_until' in user) userData.manual_presence_until = user.manualPresenceUntil ?? user.manual_presence_until ?? null;
    if ('manualPresenceClearMode' in user || 'manual_presence_clear_mode' in user) userData.manual_presence_clear_mode = user.manualPresenceClearMode ?? user.manual_presence_clear_mode ?? null;
    if ('manualPresenceSetAt' in user || 'manual_presence_set_at' in user) userData.manual_presence_set_at = user.manualPresenceSetAt ?? user.manual_presence_set_at ?? null;
    // Every status picker writes preferences.manualPresence, but other people's
    // presence is read from the manual_presence_* columns. Mirror the nested
    // entry into them so the two cannot drift; it wins over the top-level
    // fields above because those are only ever the values loaded with the user.
    if (user.preferences && 'manualPresence' in user.preferences) {
      const columns = manualEntryToColumns(user.preferences.manualPresence);
      userData.manual_presence_status = columns.manualPresenceStatus;
      userData.manual_presence_label = columns.manualPresenceLabel;
      userData.manual_presence_until = columns.manualPresenceUntil;
      userData.manual_presence_clear_mode = columns.manualPresenceClearMode;
      userData.manual_presence_set_at = columns.manualPresenceSetAt;
    }
    if (user.last_login || user.lastLogin) userData.last_login = user.last_login || user.lastLogin;
    if (user.access_level || user.accessLevel) userData.access_level = user.access_level || user.accessLevel;
    if (user.parent_user_id || user.parentUserId) userData.parent_user_id = user.parent_user_id || user.parentUserId;
    if (user.assigned_projects || user.assignedProjects) userData.assigned_projects = user.assigned_projects || user.assignedProjects;
    if (user.supervisor_role || user.supervisorRole) userData.supervisor_role = user.supervisor_role || user.supervisorRole;
    if (user.oauth_provider || user.oauthProvider) userData.oauth_provider = user.oauth_provider || user.oauthProvider;
    if ('call_availability' in user) userData.call_availability = user.call_availability;
    if ('emergencyContact' in user || 'emergency_contact' in user) userData.emergency_contact = user.emergencyContact ?? user.emergency_contact ?? null;
    // Structured emergency contact — canonical columns on users, shared with the Employees register.
    if ('emergencyContactName' in user || 'emergency_contact_name' in user) userData.emergency_contact_name = user.emergencyContactName ?? user.emergency_contact_name ?? null;
    if ('emergencyContactPhone' in user || 'emergency_contact_phone' in user) userData.emergency_contact_phone = user.emergencyContactPhone ?? user.emergency_contact_phone ?? null;
    if ('emergencyContactRelationship' in user || 'emergency_contact_relationship' in user) userData.emergency_contact_relationship = user.emergencyContactRelationship ?? user.emergency_contact_relationship ?? null;
    // Timezone: top-level column mirrors preferences.workingSchedule.timezone
    const tz = user.timezone || user.preferences?.workingSchedule?.timezone;
    if (tz) userData.timezone = tz;

    return userData;
  }

  protected transformMainContractorForBackend(contractor: any): any {
    // Transform main contractor data from camelCase to snake_case for backend
    const contractorData: any = {
      id: contractor.id,
      name: contractor.name,
      entity_type: contractor.entityType || contractor.entity_type || 'limited_company',
      status: contractor.status || 'active',
      created_at: contractor.createdAt,
      updated_at: contractor.updatedAt,
    };

    // Handle address field - database expects single 'address' field, not separate lines
    if (contractor.address) {
      // Already has single address field (from backend data)
      contractorData.address = contractor.address;
    } else {
      // Has separate address lines (from frontend form) - combine them
      const addressParts = [
        contractor.addressLine1,
        contractor.addressLine2,
        contractor.addressLine3
      ].filter(Boolean);
      
      if (addressParts.length > 0) {
        contractorData.address = addressParts.join('\n');
      }
    }
    
    if (contractor.postcode) contractorData.postcode = contractor.postcode;

    // Contact fields
    if (contractor.phone) contractorData.phone = contractor.phone;
    if (contractor.email) contractorData.email = contractor.email;
    if (contractor.website) contractorData.website = contractor.website;

    // Company information
    if (contractor.tradingName || contractor.trading_name) contractorData.trading_name = contractor.tradingName || contractor.trading_name;
    if (contractor.primaryContactName || contractor.primary_contact_name) contractorData.primary_contact_name = contractor.primaryContactName || contractor.primary_contact_name;
    if (contractor.companyNumber) contractorData.company_number = contractor.companyNumber;
    if (contractor.creditRating) contractorData.credit_rating = contractor.creditRating;
    if (contractor.paymentHistory) contractorData.payment_history = contractor.paymentHistory;
    if (contractor.averagePaymentDays !== undefined) contractorData.average_payment_days = contractor.averagePaymentDays;

    // Project metrics
    if (contractor.activeProjects !== undefined) contractorData.active_projects = contractor.activeProjects;
    if (contractor.completedProjects !== undefined) contractorData.completed_projects = contractor.completedProjects;
    if (contractor.totalValueWorked !== undefined) contractorData.total_value_worked = contractor.totalValueWorked;

    // Risk and compliance
    if (contractor.riskFlags) contractorData.risk_flags = contractor.riskFlags;
    if (contractor.notes) contractorData.notes = contractor.notes;

    // Geocoding fields
    if (contractor.latitude !== undefined) contractorData.latitude = contractor.latitude;
    if (contractor.longitude !== undefined) contractorData.longitude = contractor.longitude;
    if (contractor.geocodedAt) contractorData.geocoded_at = contractor.geocodedAt;

    if (contractor.vatNumber !== undefined) contractorData.vat_number = contractor.vatNumber || null;
    if (contractor.billingEmail !== undefined) contractorData.billing_email = contractor.billingEmail || null;
    if (contractor.billingAddress !== undefined) contractorData.billing_address = contractor.billingAddress;
    if (contractor.cisVerification !== undefined) contractorData.cis_verification = contractor.cisVerification;

    return contractorData;
  }

  protected transformMainContractorContactForBackend(contact: any): any {
    // Transform main contractor contact data from camelCase to snake_case for backend
    const normalizedPosition =
      typeof contact.position === 'string' && contact.position.trim()
        ? contact.position.trim()
        : 'Contact';

    const normalizedPhone =
      typeof contact.phone === 'string'
        ? contact.phone.trim()
        : '';

    const contactData: any = {
      id: contact.id,
      main_contractor_id: contact.mainContractorId,
      name: typeof contact.name === 'string' ? contact.name.trim() : '',
      position: normalizedPosition,
      email: typeof contact.email === 'string' ? contact.email.trim() : '',
      phone: normalizedPhone,
      is_primary: contact.isPrimary ?? contact.is_primary ?? false,
      created_at: contact.createdAt || contact.created_at || new Date().toISOString(),
    };

    if (typeof contact.mobile === 'string' && contact.mobile.trim()) {
      contactData.mobile = contact.mobile.trim();
    }
    if (typeof contact.notes === 'string' && contact.notes.trim()) {
      contactData.notes = contact.notes.trim();
    }

    return contactData;
  }

  protected transformClientRequestForBackend(request: any): any {
    const firstDefined = (...values: any[]) => values.find(value => value !== undefined);

    // Transform client request data to match backend schema
    const requestData: any = {
      id: request.id,
      company_name: firstDefined(request.company_name, request.companyName),
      company_address: firstDefined(request.company_address, request.companyAddress),
      company_postcode: firstDefined(request.company_postcode, request.companyPostcode),
      company_phone: firstDefined(request.company_phone, request.companyPhone),
      company_email: firstDefined(request.company_email, request.companyEmail),
      contact_name: firstDefined(request.contact_name, request.contactName),
      contact_email: firstDefined(request.contact_email, request.contactEmail),
      contact_phone: firstDefined(request.contact_phone, request.contactPhone),
      contact_position: firstDefined(request.contact_position, request.contactPosition),
      requested_by: firstDefined(request.requestedBy, request.requested_by),
      requested_by_name: firstDefined(request.requestedByName, request.requested_by_name),
      request_reason: firstDefined(request.requestReason, request.request_reason, ''), // Map requestReason to request_reason
      status: request.status || 'pending',
      created_at: firstDefined(request.createdAt, request.created_at),
      updated_at: firstDefined(request.updatedAt, request.updated_at),
    };

    // Optional fields
    const companyNumber = firstDefined(request.company_number, request.companyNumber);
    const billingTier = firstDefined(request.billing_tier, request.billingTier);
    const billingFrequency = firstDefined(request.billing_frequency, request.billingFrequency);
    const subscriptionPrice = firstDefined(request.subscription_price, request.subscriptionPrice);
    const reviewedBy = firstDefined(request.reviewedBy, request.reviewed_by);
    const reviewedByName = firstDefined(request.reviewedByName, request.reviewed_by_name);
    const reviewedAt = firstDefined(request.reviewedAt, request.reviewed_at);
    const reviewNotes = firstDefined(request.reviewNotes, request.review_notes);
    const createdClientId = firstDefined(request.createdClientId, request.created_client_id);

    if (companyNumber) requestData.company_number = companyNumber;
    if (request.website) requestData.website = request.website;
    if (billingTier) requestData.billing_tier = billingTier;
    if (billingFrequency) requestData.billing_frequency = billingFrequency;
    if (subscriptionPrice !== undefined) requestData.subscription_price = subscriptionPrice;
    if (request.notes) requestData.notes = request.notes;
    if (reviewedBy) requestData.reviewed_by = reviewedBy;
    if (reviewedByName) requestData.reviewed_by_name = reviewedByName;
    if (reviewedAt) requestData.reviewed_at = reviewedAt;
    if (reviewNotes) requestData.review_notes = reviewNotes;
    
    // Only include created_client_id if the company exists in the database
    if (createdClientId && this.db.companies[createdClientId]) {
      requestData.created_client_id = createdClientId;
    }

    return requestData;
  }

  protected transformTaskForBackend(task: any): any {
    // Transform task data to match backend schema
    // Remove computed fields that don't exist in the database
    
    // Map priority values to match database enum constraint
    // Database expects: 'low', 'medium', 'high', 'critical', 'completed'
    const priorityMap: { [key: string]: string } = {
      'low': 'low',
      'medium': 'medium',
      'high': 'high',
      'urgent': 'critical', // Map urgent to critical for database
      'critical': 'critical',
      'completed': 'completed', // Special status for completed tasks
    };

    // Map status values to match database enum constraint
    // Database expects: 'pending', 'in-progress', 'awaiting_response', 'completed', 'cancelled'
    const statusMap: { [key: string]: string } = {
      'pending': 'pending',
      'in_progress': 'in-progress', // Map underscore to hyphen
      'in-progress': 'in-progress',
      'awaiting_response': 'awaiting_response', // Preserve the scheduler/calendar storage value
      'awaiting-response': 'awaiting_response',
      'completed': 'completed',
      'cancelled': 'cancelled',
    };

    // Map type values to match database enum constraint
    // Database expects: 'deadline', 'followup', 'review', 'submission', 'approval', 'general'
    const typeMap: { [key: string]: string } = {
      'email': 'followup',
      'phone_call': 'followup',
      'chase': 'followup',
      'follow_up': 'followup',
      'measure': 'general',
      'review_contract': 'review',
      'review_document': 'review',
      'send_document': 'submission',
      'meeting': 'general',
      'site_visit': 'general',
      'calculation': 'general',
      'pricing': 'general',
      'submission': 'submission',
      'deadline': 'deadline',
      'followup': 'followup',
      'review': 'review',
      'approval': 'approval',
      'general': 'general',
      'other': 'general',
    };

    // Map source values to match database enum constraint
    // Database expects: 'manual', 'payment-app', 'variation', 'cvi', 'rfi', 'eot', 'tender', 'document', 'project', 'retention'
    const sourceMap: { [key: string]: string } = {
      'email': 'manual',
      'phone': 'manual',
      'message': 'manual',
      'meeting': 'manual',
      'internal': 'manual',
      'client_portal': 'manual',
      'manual': 'manual',
      'payment-app': 'payment-app',
      'variation': 'variation',
      'cvi': 'cvi',
      'rfi': 'rfi',
      'eot': 'eot',
      'tender': 'tender',
      'document': 'document',
      'project': 'project',
      'retention': 'retention',
    };

    const taskData: any = {
      id: task.id,
      title: task.title,
      description: task.description,
      source: sourceMap[task.source] || 'manual',
      type: typeMap[task.type] || 'general',
      status: statusMap[task.status] || 'pending',
      priority: priorityMap[task.priority] || 'medium',
      created_by: task.created_by,
      created_at: task.created_at,
      updated_at: task.updated_at,
    };

    // Store the original frontend type in source_details so we can retrieve it later
    // This preserves the specific type (email, phone_call, etc.) even though backend only stores simplified types
    const originalType = task.type;
    const sourceDetailsWithType = task.source_details 
      ? `${task.source_details}|original_type:${originalType}`
      : `original_type:${originalType}`;

    // Optional fields that exist in the database schema
    if (task.due_date) taskData.due_date = task.due_date;
    if (task.completed_at) taskData.completed_at = task.completed_at;
    // Map completed_date to completed_at for backend. Checked explicitly
    // (not truthy) so reopening a task can send `null` to clear it.
    if (task.completed_date !== undefined) taskData.completed_at = task.completed_date;
    if (task.assigned_to) taskData.assigned_to = task.assigned_to;
    if (task.related_entity_type) taskData.related_entity_type = task.related_entity_type;
    if (task.related_entity_id) taskData.related_entity_id = task.related_entity_id;
    if (task.tags) taskData.tags = task.tags;
    if (task.notes !== undefined) taskData.notes = task.notes;
    taskData.source_details = sourceDetailsWithType; // Always include to preserve original type
    if (task.visible_to_client !== undefined) taskData.visible_to_client = task.visible_to_client;
    if (task.client_can_edit !== undefined) taskData.client_can_edit = task.client_can_edit;
    
    // Always include project_id if it's provided (trust the frontend value)
    if (task.project_id) {
      taskData.project_id = task.project_id;
    }

    // NOTE: Fields NOT included (computed/denormalized fields):
    // - created_by_name (lookup from users table)
    // - assigned_to_name (lookup from users table)
    // - project_name (lookup from projects table)
    // - client_company_id (lookup from projects table)
    // - client_name (lookup from companies table)

    return taskData;
  }

  protected transformProjectPlannerItemForBackend(item: any): any {
    const normalizedDisplayOrder = Number(item.displayOrder ?? item.display_order);
    const estimatedHours = Number(item.estimatedHours ?? item.estimated_hours);
    const timeLogMinutes = Number(item.timeLogMinutes ?? item.time_log_minutes);
    const cost = Number(item.cost);
    const actualCost = Number(item.actualCost ?? item.actual_cost);
    const plannerData: any = {
      id: item.id,
      project_id: item.projectId || item.project_id,
      title: item.title,
      item_type: item.itemType || item.item_type || 'task',
      start_date: (item.startDate || item.start_date || new Date().toISOString()).slice(0, 10),
      end_date: (item.endDate || item.end_date || item.startDate || item.start_date || new Date().toISOString()).slice(0, 10),
      status: item.status || 'not_started',
      progress: typeof item.progress === 'number' ? item.progress : Number(item.progress || 0),
      updated_at: item.updatedAt || item.updated_at || new Date().toISOString(),
    };

    if (item.description !== undefined) plannerData.description = item.description;
    if (item.notes !== undefined) plannerData.notes = item.notes;
    if ((item.ownerName || item.owner_name) !== undefined) plannerData.owner_name = item.ownerName || item.owner_name;
    if ((item.workPackage || item.work_package) !== undefined) plannerData.work_package = item.workPackage || item.work_package;
    if ((item.workZone || item.work_zone) !== undefined) plannerData.work_zone = item.workZone || item.work_zone;
    if (item.wbsNumber !== undefined || item.wbs_number !== undefined) {
      plannerData.wbs_number = String(item.wbsNumber ?? item.wbs_number ?? '');
    }
    if (item.priority !== undefined) plannerData.priority = item.priority;
    if (Number.isFinite(normalizedDisplayOrder)) {
      plannerData.display_order = Math.max(0, Math.floor(normalizedDisplayOrder));
    }
    if (item.deadlineDate !== undefined || item.deadline_date !== undefined) {
      const deadlineValue = item.deadlineDate ?? item.deadline_date;
      if (deadlineValue) {
        plannerData.deadline_date = String(deadlineValue).slice(0, 10);
      }
    }
    if (Number.isFinite(estimatedHours)) plannerData.estimated_hours = Math.max(0, estimatedHours);
    if (Number.isFinite(timeLogMinutes)) plannerData.time_log_minutes = Math.max(0, Math.round(timeLogMinutes));
    if (Number.isFinite(cost)) plannerData.cost = Math.max(0, cost);
    if (Number.isFinite(actualCost)) plannerData.actual_cost = Math.max(0, actualCost);
    const toPlannerLinkType = (value: unknown): 'FS' | 'SS' | 'FF' | 'SF' => {
      const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
      if (normalized === 'SS' || normalized === 'FF' || normalized === 'SF') return normalized;
      return 'FS';
    };
    const typedLinksSource = item.predecessorLinks ?? item.predecessor_links;
    const typedLinks = Array.isArray(typedLinksSource)
      ? typedLinksSource
        .map((entry: unknown) => {
          if (!entry || typeof entry !== 'object') return null;
          const raw = entry as Record<string, unknown>;
          const predecessorIdValue = raw.predecessorId ?? raw.predecessor_id ?? raw.id;
          const predecessorId = typeof predecessorIdValue === 'string' || typeof predecessorIdValue === 'number'
            ? String(predecessorIdValue).trim()
            : '';
          if (!predecessorId) return null;
          return {
            predecessorId,
            linkType: toPlannerLinkType(raw.linkType ?? raw.link_type ?? raw.type),
          };
        })
        .filter((entry): entry is { predecessorId: string; linkType: 'FS' | 'SS' | 'FF' | 'SF' } => !!entry)
      : [];

    if (typedLinks.length > 0) {
      plannerData.predecessor_ids = typedLinks;
    } else if (item.predecessorIds !== undefined || item.predecessor_ids !== undefined) {
      const predecessorIds = item.predecessorIds || item.predecessor_ids || [];
      plannerData.predecessor_ids = Array.isArray(predecessorIds)
        ? predecessorIds
          .map((entry: unknown) => {
            if (typeof entry === 'string' || typeof entry === 'number') {
              return String(entry).trim();
            }
            if (entry && typeof entry === 'object') {
              const raw = entry as Record<string, unknown>;
              const predecessorIdValue = raw.predecessorId ?? raw.predecessor_id ?? raw.id;
              if (typeof predecessorIdValue === 'string' || typeof predecessorIdValue === 'number') {
                return String(predecessorIdValue).trim();
              }
            }
            return '';
          })
          .filter(Boolean)
        : [];
    }
    if (item.attachments !== undefined) plannerData.attachments = item.attachments || [];
    if ((item.color || item.colour) !== undefined) plannerData.color = item.color || item.colour;
    if ((item.createdBy || item.created_by) !== undefined) plannerData.created_by = item.createdBy || item.created_by;
    if ((item.updatedBy || item.updated_by) !== undefined) plannerData.updated_by = item.updatedBy || item.updated_by;
    if ((item.createdAt || item.created_at) !== undefined) plannerData.created_at = item.createdAt || item.created_at;
    if ((item.parentId || item.parent_id) !== undefined) plannerData.parent_id = item.parentId || item.parent_id || null;

    return plannerData;
  }

  /**
   * Transform task comment data to match backend schema
   */
  protected transformTaskCommentForBackend(comment: any): any {
    // The backend table uses 'content' instead of 'comment'
    // Note: visible_to_client is not supported by the current backend schema
    return {
      id: comment.id,
      task_id: comment.task_id,
      user_id: comment.user_id,
      user_name: comment.user_name,
      content: comment.comment, // Map 'comment' to 'content'
      created_at: comment.created_at,
    };
  }

  /**
   * Force immediate save to Google Drive (DEPRECATED - now using Supabase)
   */
  public async forceSaveToGoogleDrive(): Promise<void> {
    // Silently does nothing - Google Drive database storage is deprecated, now using Supabase
    return Promise.resolve();
  }

  /**
   * Initialize database with demo data
   */
  public initializeWithDemoData(demoData: Partial<Database>): void {
    this.db = {
      ...this.createEmptyDatabase(),
      ...demoData,
      version: VERSION,
      lastUpdated: new Date().toISOString(),
    };
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Reset database to empty state
   */
  public resetDatabase(): void {
    this.db = this.createEmptyDatabase();
    this.saveToStorage();
    this.notifyListeners();
  }

  /**
   * Get database statistics
   */
  public getStats() {
    return {
      companies: Object.keys(this.db.companies || {}).length,
      users: Object.keys(this.db.users || {}).length,
      projects: Object.keys(this.db.projects || {}).length,
      contractItems: Object.keys(this.db.contractItems || {}).length,
      tasks: Object.keys(this.db.tasks || {}).length,
      projectPlannerItems: Object.keys(this.db.projectPlannerItems || {}).length,
      tenders: Object.keys(this.db.tenders || {}).length,
      interimApplications: Object.keys(this.db.interimApplications || {}).length,
      activities: Object.keys(this.db.activities || {}).length,
      lastUpdated: this.db.lastUpdated,
      version: this.db.version,
    };
  }

  /**
   * Export database as JSON
   */
  public exportDatabase(): string {
    return JSON.stringify(this.db, null, 2);
  }

  /**
   * Import database from JSON
   */
  public importDatabase(jsonData: string): void {
    try {
      const imported = JSON.parse(jsonData);
      this.db = imported;
      this.saveToStorage();
      this.notifyListeners();
    } catch (error) {
      console.error('❌ Error importing database:', error);
      throw new Error('Invalid database format');
    }
  }

  // ============================================================================
  // SUBSCRIPTION & REACTIVITY
  // ============================================================================

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  protected notifyListeners(): void {
    // Debounce: batch rapid successive calls into a single notification.
    // This prevents a cascade of re-renders when multiple writes happen in quick
    // succession (e.g. initial data load, Realtime burst, batch operations).
    if (this.notifyDebounceTimer !== null) {
      clearTimeout(this.notifyDebounceTimer);
    }
    this.notifyDebounceTimer = setTimeout(() => {
      this.notifyDebounceTimer = null;
      this.listeners.forEach(listener => listener());
    }, 80);
  }

  /**
   * Check if DataService has been initialized with data
   */
  public getDataSyncState(): { refreshing: boolean; error: string | null } {
    return { refreshing: this.refreshing, error: this.syncError };
  }

  public getInitialLoadState(): 'loading' | 'ready' | 'error' {
    return this.demoMode ? 'ready' : this.initialLoadState;
  }

  public isDataReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Wait for DataService to finish initialization
   */
  public async waitForInitialization(): Promise<void> {
    if (this.initializationPromise) {
      await this.initializationPromise;
    }
  }

  /**
   * Refresh data from backend server (force sync)
   */
  public refreshFromBackend(force = false): Promise<void> {
    if (this.backendRefreshPromise) {
      if (!force || !this.backendRequestStarted) return this.backendRefreshPromise;
      if (this.queuedForcedRefresh) return this.queuedForcedRefresh;
      const generation = this.identityGeneration;
      const queued = this.backendRefreshPromise.then(async () => {
        if (this.queuedForcedRefresh === queued) this.queuedForcedRefresh = null;
        if (generation === this.identityGeneration) await this.performBackendRefresh(true);
      });
      this.queuedForcedRefresh = queued;
      this.backendRefreshPromise = queued;
      void queued.finally(() => {
        if (this.queuedForcedRefresh === queued) this.queuedForcedRefresh = null;
        if (this.backendRefreshPromise === queued) this.backendRefreshPromise = null;
      });
      return queued;
    }
    const pending = this.performBackendRefresh(force);
    this.backendRefreshPromise = pending;
    void pending.finally(() => {
      if (this.backendRefreshPromise === pending) this.backendRefreshPromise = null;
    });
    return pending;
  }

  private async authenticatedSnapshotSession(): Promise<{ owner: string; token: string } | null> {
    return new Promise(resolve => {
      const timer = setTimeout(() => resolve(null), 5_000);
      const finish = (session: { user?: { id?: string }; access_token?: string } | null) => {
        clearTimeout(timer);
        resolve(session?.user?.id && session.access_token
          ? { owner: session.user.id, token: session.access_token } : null);
      };
      void getSupabaseClient().auth.getSession().then(({ data }) => finish(data.session), () => finish(null));
    });
  }

  private async performBackendRefresh(force: boolean): Promise<void> {
    await this.waitForInitialization();
    const generation = this.identityGeneration;
    if (!this.useSupabase) {
      console.warn('⚠️ Backend sync not enabled');
      return;
    }

    if (!force && Date.now() < this.retryAfter) return;
    this.refreshing = true;
    this.notifyListeners();
    const session = await this.authenticatedSnapshotSession();
    if (generation !== this.identityGeneration) return;
    if (!session) {
      this.refreshing = false;
      if (this.initialLoadState === 'loading') this.initialLoadState = 'error';
      this.syncError = 'Your session could not be checked. Please try again.';
      this.retryAfter = Date.now() + 30_000;
      this.notifyListeners();
      return;
    }
    const { owner, token } = session;

    // THROTTLE: Don't hammer the backend with full DB fetches. 
    // If a refresh happened recently, skip this one. Realtime handles granular updates.
    const now = Date.now();
    if (!force && now - this.lastBackendRefreshTime < this.REFRESH_THROTTLE_MS) {
      this.refreshing = false;
      this.notifyListeners();
      return;
    }
    try {
      // The shared request is cancelled on page unload or account changes.
      const signal = this.createFetchController(30_000);

      // Same includeArchived=true as syncFromSupabase — keeps the archived
      // "view archived" toggle over this cache working after a refresh.
      this.backendRequestStarted = true;
      const requestedAt = ++this.syncSequence;
      const response = await fetch(`${API_BASE}/database?includeArchived=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal,
      });

      if (generation !== this.identityGeneration) return;
      if (response.ok) {
        // Demo mode may have engaged while the fetch was in flight (a sign-in
        // race). Drop the response on the floor — we must never trample the
        // fixture with whatever the real backend returned.
        if (this.demoMode || !this.useSupabase) return;

        const supabaseData = await response.json();
        const currentSession = await this.authenticatedSnapshotSession();
        if (generation !== this.identityGeneration || currentSession?.owner !== owner) return;

        // Runtime-authenticated responses can establish ownership even when
        // the optional synchronous profile mirror could not be persisted.
        if (this.snapshotOwner !== owner) this.db = this.createEmptyDatabase();
        this.snapshotOwner = owner;
        this.snapshotRuntimeOwner = owner;

        // Transform backend data to frontend format
        const transformedData = this.transformDataFromBackend(supabaseData);

        // A successful authenticated snapshot can legitimately be empty.
        this.mergeLocalPlannerItems(transformedData);
        this.keepUnsyncedLocalWrites(transformedData, requestedAt);
        this.db = this.migrateDatabase(transformedData);
        this.initialLoadState = 'ready';
        this.syncError = null;
        this.retryAfter = 0;
        this.saveToLocalStorage();
        this.lastBackendRefreshTime = Date.now();
        this.notifyListeners();
      } else if (response.status === 404) {
        this.syncError = 'The dashboard service is temporarily unavailable. Please try again.';
        // Only swallow when the Supabase gateway itself can't route the request
        // (body: {"error":"Failed to fetch <path>"}). A real route returning 404
        // (e.g. "project not found") must not be silenced — that was the same
        // misdiagnosis that hid the BOQ bulkUpdate route bug for weeks.
        let body404: any = null;
        try { body404 = await response.clone().json(); } catch { /* non-JSON body */ }
        const isGateway404 = typeof body404?.error === 'string' && body404.error.startsWith('Failed to fetch');
        if (!isGateway404) {
          console.error(`refreshFromBackend: real 404 from backend (not a missing route):`, body404);
        }
      } else {
        this.syncError = response.status === 401 || response.status === 403
          ? 'Your session could not access the dashboard. Please try again.'
          : 'Some project data is temporarily unavailable. Please try again.';
        // Only log non-404 errors with detailed information
        console.error(`Failed to refresh from backend: ${response.status} ${response.statusText || 'Unknown Error'}`);
        // Try to get error details from response body
        try {
          const errorText = await response.text();
          if (errorText) {
            console.error('Backend error details:', errorText);
          }
        } catch (e) {
          // Ignore if we can't read the error body
        }
      }
    } catch (error: any) {
      // AbortError means we intentionally cancelled (page unload, new request, etc.)
      if (generation === this.identityGeneration) this.syncError = error?.name === 'AbortError'
        ? 'The dashboard took too long to respond. Please try again.'
        : 'The dashboard could not connect. Check your connection and try again.';
      if (error?.name === 'AbortError') return;
      // Silently handle network errors during polling - not critical
      if (error instanceof TypeError && error.message?.includes('fetch')) {
        // Network error - don't spam console
        return;
      }
      console.error('Error refreshing from backend:', error);
    } finally {
      if (generation === this.identityGeneration) {
        this.backendRequestStarted = false;
        this.refreshing = false;
        if (this.syncError) this.retryAfter = Date.now() + 30_000;
        this.notifyListeners();
      }
      if (generation === this.identityGeneration && this.initialLoadState === 'loading') {
        this.initialLoadState = 'error';
        this.notifyListeners();
      }
    }
  }

  /**
   * Debounced refresh to prevent hammering the backend when multiple changes occur
   */
  protected debouncedRealtimeRefresh(): void {
    if (this.realtimeRefreshTimeout) {
      clearTimeout(this.realtimeRefreshTimeout);
    }
    
    this.realtimeRefreshTimeout = setTimeout(async () => {
      try {
        await this.refreshFromBackend();
      } catch (error) {
        console.error('Error in debounced realtime refresh:', error);
      }
    }, 500); // Wait 500ms after last change before refreshing
  }

  /**
   * Setup Realtime WebSocket subscriptions for push-based updates
   * Replaces inefficient polling with true realtime sync
   */
  protected setupRealtimeSubscriptions(): void {
    if (!this.useSupabase) return;
    
    const supabase = getSupabaseClient();
    
    // ============================================================================
    // COMPANIES TABLE SUBSCRIPTION
    // ============================================================================
    const companiesChannel = supabase
      .channel('companies_realtime')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'companies'
        },
        async (payload) => {

          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const companyId = payload.new?.id;
            if (companyId) {
              // Immediately merge topup_credits_* fields from the realtime payload
              // directly into the local cache. This is resilient to 401s on
              // fetchCompany (e.g. token race on return from Stripe) and ensures
              // credits are reflected instantly after completeCreditTopup writes to DB.
              if (payload.new && this.db.companies[companyId]) {
                const { new: n } = payload as any;
                const existing = this.db.companies[companyId];
                if (n.topup_credits_total !== undefined)        existing.topup_credits_total = n.topup_credits_total;
                if (n.topup_credits_used !== undefined)         existing.topup_credits_used = n.topup_credits_used;
                if (n.topup_credits_billing_period !== undefined) existing.topup_credits_billing_period = n.topup_credits_billing_period;
                this.saveToLocalStorage();
                this.notifyListeners();
              }
              // Also attempt full refresh from backend for all other fields
              try {
                await (this as any).fetchCompany(companyId);
              } catch (error) {
                console.error('Error fetching updated company:', error);
              }
            }
          } else if (payload.eventType === 'DELETE') {
            // Remove from local cache and trigger debounced refresh
            const companyId = payload.old?.id;
            if (companyId && this.db.companies[companyId]) {
              delete this.db.companies[companyId];
              this.notifyListeners();
              this.saveToLocalStorage();
            }
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Realtime subscription active for companies
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Live events unavailable — converge via a one-shot refresh so the
          // UI never sits stale behind a dead channel.
          this.debouncedRealtimeRefresh();
        }
      });
    
    // ============================================================================
    // PROJECTS TABLE SUBSCRIPTION
    // ============================================================================
    const handleProjectRealtimePayload = async (payload: any) => {
      if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
        // Re-hydrate through the scoped endpoint rather than accepting the raw
        // Realtime row. Internal subscriptions can observe every tenant, while
        // the endpoint deliberately removes demo/test projects from operations.
        this.debouncedRealtimeRefresh();
      } else if (payload.eventType === 'DELETE') {
        // Remove from local cache and trigger debounced refresh
        const projectId = payload.old?.id;
        if (projectId && this.db.projects[projectId]) {
          delete this.db.projects[projectId];
          this.notifyListeners();
          this.saveToLocalStorage();
        }
        this.debouncedRealtimeRefresh();
      }
    };

    const projectsChannel = supabase
      .channel('projects_realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects'
        },
        handleProjectRealtimePayload
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Realtime subscription active for projects
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Live events unavailable — converge via a one-shot refresh so the
          // UI never sits stale behind a dead channel.
          this.debouncedRealtimeRefresh();
        }
      });

    // Legacy projects table channel - keeps live updates working during table migration.
    const legacyProjectsChannel = supabase
      .channel('projects_legacy_realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects_a476273b'
        },
        handleProjectRealtimePayload
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          // Legacy table not present in some environments; safe to ignore.
        }
      });
    
    // ============================================================================
    // USERS TABLE SUBSCRIPTION
    // ============================================================================
    const usersChannel = supabase
      .channel('users_realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'users'
        },
        async (payload) => {
          
          // Use debounced refresh to avoid multiple rapid backend calls
          this.debouncedRealtimeRefresh();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Realtime subscription active for users
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Live events unavailable — converge via a one-shot refresh so the
          // UI never sits stale behind a dead channel.
          this.debouncedRealtimeRefresh();
        }
      });
    
    // ============================================================================
    // TASKS TABLE SUBSCRIPTION
    // ============================================================================
    const tasksChannel = supabase
      .channel('tasks_realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks'
        },
        async (payload) => {
          
          // Use debounced refresh to avoid multiple rapid backend calls
          this.debouncedRealtimeRefresh();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Realtime subscription active for tasks
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Live events unavailable — converge via a one-shot refresh so the
          // UI never sits stale behind a dead channel.
          this.debouncedRealtimeRefresh();
        }
      });

    // ============================================================================
    // PROJECT PLANNER ITEMS TABLE SUBSCRIPTION
    // ============================================================================
    const projectPlannerItemsChannel = supabase
      .channel('project_planner_items_realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_planner_items'
        },
        async (payload) => {
          // Use debounced refresh to avoid multiple rapid backend calls
          this.debouncedRealtimeRefresh();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Realtime subscription active for project planner items
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Live events unavailable — converge via a one-shot refresh so the
          // UI never sits stale behind a dead channel.
          this.debouncedRealtimeRefresh();
        }
      });
    
    // ============================================================================
    // ACTIVITIES TABLE SUBSCRIPTION
    // ============================================================================
    const activitiesChannel = supabase
      .channel('activities_realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'activities'
        },
        async (payload) => {
          
          // Use debounced refresh to avoid multiple rapid backend calls
          this.debouncedRealtimeRefresh();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Realtime subscription active for activities
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Live events unavailable — converge via a one-shot refresh so the
          // UI never sits stale behind a dead channel.
          this.debouncedRealtimeRefresh();
        }
      });
    
    // ============================================================================
    // NOTIFICATIONS TABLE SUBSCRIPTION
    // ============================================================================
    const notificationsChannel = supabase
      .channel('notifications_realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications'
        },
        async (payload) => {
          
          // Use debounced refresh to avoid multiple rapid backend calls
          this.debouncedRealtimeRefresh();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Realtime subscription active for notifications
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Live events unavailable — converge via a one-shot refresh so the
          // UI never sits stale behind a dead channel.
          this.debouncedRealtimeRefresh();
        }
      });
    
    this.realtimeChannels.push(
      companiesChannel, 
      projectsChannel, 
      legacyProjectsChannel,
      usersChannel, 
      tasksChannel, 
      projectPlannerItemsChannel,
      activitiesChannel, 
      notificationsChannel
    );
  }

  /**
   * Cleanup Realtime subscriptions (useful for cleanup)
   */
  public stopRealtimeSubscriptions(): void {
    // Clear any pending debounced refresh
    if (this.realtimeRefreshTimeout) {
      clearTimeout(this.realtimeRefreshTimeout);
      this.realtimeRefreshTimeout = null;
    }
    
    // Unsubscribe from all channels
    if (this.realtimeChannels.length > 0) {
      this.realtimeChannels.forEach(channel => {
        channel.unsubscribe();
      });
      this.realtimeChannels = [];
    }
  }

  // ============================================================================
  // DEMO MODE
  // ============================================================================

  /**
   * True if the singleton is currently serving demo fixture data.
   */
  public isDemoMode(): boolean {
    return this.demoMode;
  }

  /**
   * Switch the singleton into demo mode. The in-memory `db` is replaced with
   * a fresh fixture, Supabase + Realtime are torn down, and localStorage
   * writes are gated off — so the demo session is fully isolated from the
   * production database and leaves no trace on the device.
   *
   * Called by AuthContext when a user matching DEMO_USER_EMAILS signs in.
   */
  public enterDemoMode(user: any): void {
    if (this.demoMode) return;

    // Stop talking to Supabase: disabling the flag short-circuits every
    // sync/save path that already gates on `useSupabase`.
    this.useSupabase = false;
    this.supabaseDisabledReason = 'demo mode';
    this.realtimeChannels.forEach(channel => {
      try { channel.unsubscribe(); } catch { /* noop */ }
    });
    this.realtimeChannels = [];

    // Replace the in-memory database with the curated fixture.
    this.db = buildDemoDatabase(this.createEmptyDatabase(), user);
    this.demoMode = true;
    this.isInitialized = true;

    // Seed the demo user's per-stage visibility preferences so EVERY stage
    // is visible by default. The stock default in `useActiveStagesPreferences`
    // hides client_review / final_account / submitted / closed which would
    // make several demo projects invisible until the user manually toggles
    // them on. The demo should show its full pipeline immediately.
    if (user?.id && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(
          `quantis_active_stages_${user.id}`,
          JSON.stringify([
            'enquiry_received', 'tender_in_progress', 'client_review',
            'ready_to_submit', 'submitted', 'on_hold', 'pre_contract',
            'on_site', 'final_account', 'retention_period', 'closed',
          ])
        );
      } catch { /* localStorage unavailable — fixture still renders */ }
    }

    this.notifyListeners();
  }

  /**
   * Leave demo mode and reset to a clean state. Called on sign-out, and on
   * sign-in when the next user is NOT a demo account.
   */
  public exitDemoMode(): void {
    if (!this.demoMode) return;

    this.demoMode = false;
    this.useSupabase = true;
    this.supabaseDisabledReason = null;
    this.db = this.createEmptyDatabase();
    this.notifyListeners();
  }

  /**
   * Rebind realtime channels to the current auth context (e.g. after sign-in/out).
   */
  public async restartRealtimeSubscriptions(): Promise<void> {
    if (!this.useSupabase) return;

    const supabase = getSupabaseClient();
    const token = await this.getAuthHeaderToken();

    try {
      await supabase.realtime.setAuth(token);
    } catch (error) {
      console.warn('⚠️ Failed to set realtime auth token, continuing with channel restart:', error);
    }

    this.stopRealtimeSubscriptions();
    this.setupRealtimeSubscriptions();
  }

  // ============================================================================
}
